import React, { useCallback, useEffect, useMemo, useState } from "react";
import Axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import Pagination from "../../components/Pagination";
import { getAlert } from "../../../utils/SamuiUtils";
import SearchableSelect from "../../components/SearchableSelect";
import {
  PERMISSION_MATRIX,
  PERMISSION_CRUD,
  MATRIX_KEY_SET,
} from "../../../config/orgPermissionMatrix";

const THEME_COLOR = "#E13F57";

const tableHeaderStyle = {
  backgroundColor: THEME_COLOR,
  color: "#fff",
  position: "sticky",
  top: 0,
  zIndex: 10,
  borderBottom: "2px solid rgba(0,0,0,0.08)",
};

const headerCellStyle = {
  fontWeight: "600",
  color: "#fff",
};

/** ความสูงตารางคงที่ขณะโหลด — ลดการกระตุกเมื่อสลับแท็บ */
const orgTableScrollBoxStyle = {
  maxHeight: "600px",
  minHeight: "min(52vh, 480px)",
  overflowY: "auto",
  overflowX: "hidden",
};

function OrgTableSkeletonRows({ colSpan, rows = 10 }) {
  return Array.from({ length: rows }, (_, i) => (
    <tr key={`org-sk-${i}`} style={{ borderBottom: "1px solid #f3f4f6" }}>
      <td colSpan={colSpan} className="py-2 px-3 align-middle">
        <div className="placeholder-glow">
          <span
            className="placeholder w-100 d-block rounded-1"
            style={{ minHeight: "0.95rem", backgroundColor: "#e8eaed" }}
          />
        </div>
      </td>
    </tr>
  ));
}

/** กริด 3 คอลัมน์ — minmax ต่ำสุดเล็กน้อยป้องกัน track ยุบ; การ์ดยืดเต็มความกว้างคอลัมน์ */
const covProvinceGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "0.75rem",
  width: "100%",
  minWidth: "100%",
  boxSizing: "border-box",
  alignItems: "stretch",
};

const covDistrictGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "0.75rem",
  width: "100%",
  minWidth: "100%",
  boxSizing: "border-box",
  alignItems: "stretch",
};

/** checkbox | ข้อความ — ใช้ grid ภายในแทน flex เพื่อไม่ให้คอลัมน์ข้อความยุบเป็น 0 */
const covCardLabelGridStyle = {
  display: "grid",
  gridTemplateColumns: "auto minmax(0, 1fr)",
  columnGap: "0.75rem",
  rowGap: "0.25rem",
  alignItems: "start",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
};

/** แสดงข้อความเต็ม — ห่อตามความกว้างการ์ด */
const covCardNameStyle = {
  display: "block",
  fontSize: "0.95rem",
  fontWeight: 600,
  lineHeight: 1.35,
  overflowWrap: "break-word",
  wordBreak: "normal",
};

const covCardHintStyle = {
  display: "block",
  fontSize: "0.8rem",
  lineHeight: 1.4,
  marginTop: "0.25rem",
  overflowWrap: "break-word",
  wordBreak: "normal",
};

const apiApp = () => `${process.env.REACT_APP_API_URL}/api/app`;

function authCfg() {
  return {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
    },
  };
}

function keyCfg() {
  return {
    headers: {
      key: process.env.REACT_APP_ANALYTICS_KEY || "",
    },
  };
}

async function fetchBranches() {
  const { data } = await Axios.get(`${apiApp()}/org/branches`, authCfg());
  if (data.status !== "OK") throw new Error(data.message || "โหลดสาขาไม่สำเร็จ");
  return data.results || [];
}

async function fetchCoverage(branchId) {
  const q = branchId ? `?branch_id=${encodeURIComponent(branchId)}` : "";
  const { data } = await Axios.get(`${apiApp()}/org/coverage${q}`, authCfg());
  if (data.status !== "OK") throw new Error(data.message || "โหลดพื้นที่ไม่สำเร็จ");
  return data.results || [];
}

async function fetchRoles() {
  const { data } = await Axios.get(`${apiApp()}/org/comp-roles`, authCfg());
  if (data.status !== "OK") throw new Error(data.message || "โหลดบทบาทไม่สำเร็จ");
  return data.results || [];
}

async function fetchProvinces() {
  const { data } = await Axios.get(
    `${process.env.REACT_APP_API_URL}/api/provinces`,
    keyCfg()
  );
  if (data.status !== "OK") return [];
  return data.data || [];
}

async function fetchDistricts(provinceId) {
  const pidNum = parseInt(provinceId, 10);
  if (!Number.isFinite(pidNum)) return [];
  const { data } = await Axios.get(
    `${process.env.REACT_APP_API_URL}/api/districts/${pidNum}`,
    keyCfg()
  );
  if (data.status !== "OK") return [];
  return data.data || [];
}

async function searchDistrictsAll(q) {
  const qs = q ? `?q=${encodeURIComponent(q)}&limit=5000` : "";
  const { data } = await Axios.get(`${process.env.REACT_APP_API_URL}/api/districts/search${qs}`, keyCfg());
  if (data.status !== "OK") return [];
  return data.data || [];
}

async function fetchEmployees(q) {
  const qs = q ? `?q=${encodeURIComponent(q)}` : "";
  const { data } = await Axios.get(`${apiApp()}/org/employees${qs}`, authCfg());
  if (data.status !== "OK") throw new Error(data.message || "โหลดพนักงานไม่สำเร็จ");
  return data.results || [];
}

async function fetchRoleUsers(roleId) {
  const { data } = await Axios.get(`${apiApp()}/org/comp-roles/${roleId}/users`, authCfg());
  if (data.status !== "OK") throw new Error(data.message || "โหลดพนักงานไม่สำเร็จ");
  return data.results || [];
}

function normCovLevel(r) {
  return String(r.Coverage_Level || "").toUpperCase();
}

function findProvincePRow(all, provinceId) {
  const p = Number(provinceId);
  if (!Number.isFinite(p)) return null;
  return all.find((r) => normCovLevel(r) === "P" && Number(r.Province_Id) === p) || null;
}

function findDistrictDRow(all, provinceId, districtId) {
  const p = Number(provinceId);
  const d = Number(districtId);
  if (!Number.isFinite(p) || !Number.isFinite(d)) return null;
  return (
    all.find(
      (r) => normCovLevel(r) === "D" && Number(r.Province_Id) === p && Number(r.District_Id) === d
    ) || null
  );
}

function hasAnyDistrictInProvince(all, provinceId) {
  const p = Number(provinceId);
  if (!Number.isFinite(p)) return false;
  return all.some((r) => normCovLevel(r) === "D" && Number(r.Province_Id) === p);
}

function ModalShell({
  show,
  title,
  onClose,
  children,
  footer,
  dialogClassName = "modal-lg",
  dialogStyle,
  titleStyle,
}) {
  if (!show) return null;
  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block", zIndex: 1055 }}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
      >
        <div
          className={`modal-dialog modal-dialog-centered modal-dialog-scrollable ${dialogClassName}`.trim()}
          style={dialogStyle}
        >
          <div className="modal-content border-0 shadow" style={{ borderRadius: "12px" }}>
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-semibold" style={titleStyle}>{title}</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onClose}
              />
            </div>
            <div className="modal-body pt-2">{children}</div>
            {footer && <div className="modal-footer border-0 pt-0">{footer}</div>}
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1050 }}
        onClick={onClose}
        role="presentation"
      />
    </>
  );
}

const TAB_KEYS = ["branches", "roles", "users"];
const TAB_LABELS = ["สาขา", "บทบาทบริษัท", "พนักงาน"];

export default function OrgAccessAdminPage({ mode = "branches" }) {
  const [tab, setTab] = useState(mode === "roles" ? 1 : 0);
  const [branches, setBranches] = useState([]);
  const [coverageAll, setCoverageAll] = useState([]);
  const [covPendingP, setCovPendingP] = useState([]);
  const [covPendingD, setCovPendingD] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingCoverage, setLoadingCoverage] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [migrationMissing, setMigrationMissing] = useState(false);

  const [activeRoleId, setActiveRoleId] = useState("");
  const [roleEmployees, setRoleEmployees] = useState([]);
  const [loadingRoleEmployees, setLoadingRoleEmployees] = useState(false);
  const [assignEmpDialog, setAssignEmpDialog] = useState(false);
  const [assignEmpInput, setAssignEmpInput] = useState("");
  const [assignEmpOptions, setAssignEmpOptions] = useState([]);
  const [assignEmpPick, setAssignEmpPick] = useState("");

  const [branchDialog, setBranchDialog] = useState({ open: false, row: null });
  const [branchForm, setBranchForm] = useState({
    Branch_Code: "",
    Branch_Name: "",
    Branch_Address: "",
    Sort_Order: "",
    Is_Active: "Y",
  });
  const [activeBranchForm, setActiveBranchForm] = useState({
    Branch_Name: "",
    Branch_Address: "",
  });

  const [covBranchId, setCovBranchId] = useState("");
  const [covDialog, setCovDialog] = useState(false);
  const [covEditRow, setCovEditRow] = useState(null);
  const [covForm, setCovForm] = useState({
    Coverage_Level: "D",
    Province_Id: "",
    District_Id: "",
  });
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [allDistrictsCache, setAllDistrictsCache] = useState([]);
  const [loadingAllDistricts, setLoadingAllDistricts] = useState(false);
  const [covProvinceSearch, setCovProvinceSearch] = useState("");
  const [covDistrictSearch, setCovDistrictSearch] = useState("");
  // ค้นหา "จังหวัด" โดยอ้างอิงจากชื่อ "อำเภอ" ที่อยู่ในจังหวัดนั้น
  const [covProvinceDistrictSearch, setCovProvinceDistrictSearch] = useState("");
  // ค้นหา "อำเภอ" ในตาราง: ต้องกด Enter ก่อนถึงจะนำไป filter
  const [covProvinceDistrictSearchApplied, setCovProvinceDistrictSearchApplied] = useState("");
  const [provinceDistrictCache, setProvinceDistrictCache] = useState({});
  const [loadingProvinceDistrictCache, setLoadingProvinceDistrictCache] = useState(false);
  const provinceIds = useMemo(() => {
    const set = new Set();
    for (const p of provinces || []) {
      const pid = String((p?.Id ?? p?.ProvinceId) || "");
      if (pid) set.add(pid);
    }
    return Array.from(set);
  }, [provinces]);
  // ความสูงคงที่สำหรับตาราง "อำเภอ" ใน modal
  const COV_TABLE_ROW_PX = 38;
  const COV_TABLE_HEADER_PX = 58; // รวมหัวตาราง/ระยะ padding

  const filteredProvincesByDistrict = useMemo(() => {
    const term = String(covProvinceDistrictSearch || "").trim().toLowerCase();
    if (!term) return provinces;
    const expected = provinceIds.length;
    const cacheReady = expected > 0 && Object.keys(provinceDistrictCache).length === expected;
    if (!cacheReady) return provinces; // ระหว่างโหลดให้แสดงทั้งหมดก่อน
    return provinces.filter((p) => {
      const pid = String((p.Id ?? p.ProvinceId) || "");
      const names = provinceDistrictCache[pid] || [];
      return names.some((dName) => String(dName).includes(term));
    });
  }, [covProvinceDistrictSearch, provinces, provinceDistrictCache, provinceIds]);

  const covDistrictSearchTerm = useMemo(
    () => String(covProvinceDistrictSearchApplied || "").trim().toLowerCase(),
    [covProvinceDistrictSearchApplied]
  );

  const covDistrictSearchTermNormalized = useMemo(
    () => covDistrictSearchTerm.replace(/^(อ\.?\s*ำเภอ|เขต)\s*/i, "").trim(),
    [covDistrictSearchTerm]
  );

  const districtsForTable = useMemo(() => {
    const hasProvince = Boolean(covForm.Province_Id);
    if (!hasProvince && !covDistrictSearchTerm) return [];

    const base = districts || [];
    const sortByName = (arr) =>
      [...arr].sort((a, b) =>
        String(a?.NameInThai ?? a?.District_Name ?? a?.Name ?? "").localeCompare(
          String(b?.NameInThai ?? b?.District_Name ?? b?.Name ?? ""),
          "th"
        )
      );

    // กรณี "ไม่เลือกจังหวัด": backend จะกรองมาแล้วด้วยคำค้น
    if (!hasProvince) return sortByName(base);

    if (!covDistrictSearchTerm) return sortByName(base);

    return sortByName(base.filter((d) => {
      const name = String(d?.NameInThai ?? d?.District_Name ?? d?.Name ?? "");
      const nameLower = name.toLowerCase();
      return (
        nameLower.includes(covDistrictSearchTerm) ||
        (covDistrictSearchTermNormalized && nameLower.includes(covDistrictSearchTermNormalized))
      );
    }));
  }, [districts, covDistrictSearchTerm, covDistrictSearchTermNormalized, covForm.Province_Id]);

  useEffect(() => {
    // หยุดการเรียก API เพื่อทำ cache ระหว่างพิมพ์
    // เราจะกรอง “ตารางอำเภอ” ด้วยข้อมูลที่โหลดแล้วเท่านั้น
    return;
  }, [covProvinceDistrictSearch, provinces, provinceIds, provinceDistrictCache, loadingProvinceDistrictCache]);

  const [roleDialog, setRoleDialog] = useState({ open: false, row: null });
  const [roleForm, setRoleForm] = useState({
    Role_Code: "",
    Role_Name: "",
    Is_Active: "Y",
  });
  const [permDialog, setPermDialog] = useState({ open: false, role: null });
  const [permKeysSet, setPermKeysSet] = useState(() => new Set());

  const [empOptions, setEmpOptions] = useState([]);
  const [empInput, setEmpInput] = useState("");
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [userRoleRow, setUserRoleRow] = useState(null);
  const [rolePick, setRolePick] = useState("");
  const [userBranches, setUserBranches] = useState([]);
  const [branchPick, setBranchPick] = useState("");
  const [branchDefault, setBranchDefault] = useState(true);
  const [activeBranchId, setActiveBranchId] = useState("");
  const [branchPage, setBranchPage] = useState(1);
  const [branchPerPage, setBranchPerPage] = useState(10);
  const [branchRightTab, setBranchRightTab] = useState("coverage");
  const [branchContacts, setBranchContacts] = useState([]);
  const [allBranchContactsMap, setAllBranchContactsMap] = useState({});
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactDialog, setContactDialog] = useState({ open: false, row: null });
  const [contactForm, setContactForm] = useState({ Contact_Name: "", Contact_Tel: "" });

  const loadBranches = useCallback(async () => {
    setLoadingBranches(true);
    setMigrationMissing(false);
    try {
      setBranches(await fetchBranches());
    } catch (e) {
      if (e.response?.status === 503) setMigrationMissing(true);
      getAlert("FAILED", e.response?.data?.message || e.message);
      setBranches([]);
    } finally {
      setLoadingBranches(false);
    }
  }, []);

  const loadCoverage = useCallback(async () => {
    setLoadingCoverage(true);
    try {
      setCoverageAll(await fetchCoverage(null));
    } catch (e) {
      getAlert("FAILED", e.response?.data?.message || e.message);
    } finally {
      setLoadingCoverage(false);
    }
  }, []);

  const loadAllBranchContacts = useCallback(async () => {
    try {
      const res = await Axios.get(`${apiApp()}/org/branches/contacts/all`, authCfg());
      const map = {};
      (res.data?.results || []).forEach((c) => {
        if (!map[c.Branch_Id]) map[c.Branch_Id] = [];
        map[c.Branch_Id].push(c);
      });
      setAllBranchContactsMap(map);
    } catch { setAllBranchContactsMap({}); }
  }, []);

  const coverageRows = useMemo(() => {
    if (!covBranchId) return coverageAll;
    return coverageAll.filter((r) => String(r.Branch_Id) === String(covBranchId));
  }, [coverageAll, covBranchId]);

  const targetCovBranchId = Number(covBranchId || branches[0]?.Branch_Id) || null;

  const loadRoles = useCallback(async () => {
    setLoadingRoles(true);
    try {
      setRoles(await fetchRoles());
    } catch (e) {
      if (e.response?.status === 503) setMigrationMissing(true);
      getAlert("FAILED", e.response?.data?.message || e.message);
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  const loadRoleEmployees = useCallback(async (roleId) => {
    if (!roleId) { setRoleEmployees([]); return; }
    setLoadingRoleEmployees(true);
    try {
      setRoleEmployees(await fetchRoleUsers(roleId));
    } catch (e) {
      getAlert("FAILED", e.response?.data?.message || e.message);
      setRoleEmployees([]);
    } finally {
      setLoadingRoleEmployees(false);
    }
  }, []);

  useEffect(() => {
    if (mode === "roles") {
      loadRoles();
      fetchEmployees("").then(setAssignEmpOptions).catch(() => setAssignEmpOptions([]));
      return;
    }
    if (tab === 0) {
      loadBranches();
      loadCoverage();
      loadAllBranchContacts();
      fetchProvinces().then(setProvinces).catch(() => setProvinces([]));
    }
    if (tab === 1) loadRoles();
    if (tab === 2) {
      loadBranches();
      loadRoles();
      fetchEmployees("").then(setEmpOptions).catch(() => setEmpOptions([]));
    }
  }, [tab, mode, loadBranches, loadCoverage, loadRoles, loadAllBranchContacts]);

  useEffect(() => {
    if (mode !== "roles" || !roles.length) return;
    if (!activeRoleId || !roles.some((r) => String(r.Role_Id) === String(activeRoleId))) {
      setActiveRoleId(String(roles[0].Role_Id));
    }
  }, [roles, mode, activeRoleId]);

  useEffect(() => {
    if (mode === "roles") loadRoleEmployees(activeRoleId);
  }, [activeRoleId, mode, loadRoleEmployees]);

  useEffect(() => {
    if (mode !== "roles" || !activeRoleId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await Axios.get(
          `${apiApp()}/org/comp-roles/${activeRoleId}/permissions`,
          authCfg()
        );
        if (!cancelled) {
          setPermKeysSet(new Set(data.keys || []));
        }
      } catch {
        if (!cancelled) setPermKeysSet(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeRoleId, mode]);

  useEffect(() => {
    // ป้องกันค่า tab เดิมที่มากกว่าแท็บใหม่หลังรวมแท็บ
    if (tab > TAB_KEYS.length - 1) {
      setTab(0);
    }
  }, [tab]);

  // โหลด “อำเภอของจังหวัดที่เลือก” (กรณีเลือกจังหวัด)
  useEffect(() => {
    const pid = covForm.Province_Id;
    if (!pid) return;
    fetchDistricts(pid)
      .then((ds) =>
        Array.isArray(ds)
          ? ds.map((d) => ({
              ...d,
              __pid: Number(pid),
            }))
          : []
      )
      .then(setDistricts)
      .catch(() => setDistricts([]));
  }, [covForm.Province_Id]);

  // โหลด “อำเภอทุกจังหวัด” เมื่อยังไม่เลือกจังหวัด แต่มีข้อความค้นหา
  useEffect(() => {
    const pid = covForm.Province_Id;
    const term = String(covProvinceDistrictSearchApplied || "").trim().toLowerCase();
    if (pid) return; // เลือกจังหวัดแล้ว -> อยู่ในอีก effect

    if (!term) {
      setDistricts([]);
      return;
    }

    let cancelled = false;
    setLoadingAllDistricts(true);

    const termNormalized = term.replace(/^(อ\.?\s*ำเภอ|เขต)\s*/i, "").trim();
    const matchDistrict = (d) => {
      const name = String(d?.NameInThai ?? d?.District_Name ?? d?.Name ?? "");
      const nameLower = name.toLowerCase();
      if (nameLower.includes(term)) return true;
      if (termNormalized && nameLower.includes(termNormalized)) return true;
      return false;
    };

    const loadByFallback = async () => {
      // Fallback กรณี endpoint ใหม่ยังไม่ deploy (หรือเรียกไม่ได้)
      const pidList = (provinces || [])
        .map((p) => String(p?.Id ?? p?.ProvinceId ?? ""))
        .map((x) => parseInt(x, 10))
        .filter((n) => Number.isFinite(n));

      if (!pidList.length) {
        setDistricts([]);
        return;
      }

      const arr = [];
      const concurrency = 8;
      let cursor = 0;

      const worker = async () => {
        while (true) {
          const idx = cursor++;
          if (idx >= pidList.length) return;
          if (cancelled) return;
          const pid2 = pidList[idx];
          try {
            const ds = await fetchDistricts(pid2);
            const list = Array.isArray(ds) ? ds : [];
            const matched = list.filter((d) => matchDistrict(d));
            arr.push(...matched.map((d) => ({ ...d, __pid: Number(d?.ProvinceId ?? pid2) })));
          } catch {
            // ข้าม province ที่โหลดไม่ได้
          }
        }
      };

      await Promise.all(Array.from({ length: Math.min(concurrency, pidList.length) }, worker));
      if (!cancelled) {
        setDistricts(arr);
        setAllDistrictsCache(arr);
      }
    };

    (async () => {
      try {
        // Primary: ใช้ endpoint ใหม่ค้นหาข้ามทุกจังหวัด
        const ds = await searchDistrictsAll(term);
        if (cancelled) return;
        if (Array.isArray(ds) && ds.length) {
          const arr = ds.map((d) => ({ ...d, __pid: Number(d?.ProvinceId) }));
          setDistricts(arr);
          setAllDistrictsCache(arr);
          return;
        }
      } catch {
        // ถ้าล้มเหลวให้ fallback
      }

      if (!cancelled) {
        await loadByFallback();
      }
    })()
      .catch(() => {
        if (cancelled) return;
        setDistricts([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingAllDistricts(false);
      });

    return () => {
      cancelled = true;
    };
  }, [covForm.Province_Id, covProvinceDistrictSearchApplied, provinces]);

  const branchName = (id) => branches.find((b) => b.Branch_Id === id)?.Branch_Name || id;

  const getBranchAddress = (b) =>
    b?.Branch_Address ||
    b?.Address ||
    b?.Branch_Location ||
    b?.Location ||
    "-";

  const getBranchAddressRaw = (b) =>
    b?.Branch_Address ||
    b?.Address ||
    b?.Branch_Location ||
    b?.Location ||
    "";

  const getBranchContactLines = (b) => {
    const c1 = b?.Contact_Name_1 || b?.Contact1 || b?.Contact_Name || "";
    const c1Tel = b?.Contact_Tel_1 || b?.Contact1_Tel || b?.Phone || "";
    const c2 = b?.Contact_Name_2 || b?.Contact2 || "";
    const c2Tel = b?.Contact_Tel_2 || b?.Contact2_Tel || "";
    const out = [];
    if (c1 || c1Tel) out.push({ name: c1 || "-", tel: c1Tel || "-" });
    if (c2 || c2Tel) out.push({ name: c2 || "-", tel: c2Tel || "-" });
    if (!out.length) out.push({ name: "-", tel: "-" });
    return out;
  };

  useEffect(() => {
    if (!branches.length) {
      setActiveBranchId("");
      return;
    }
    const hasCurrent = branches.some((b) => String(b.Branch_Id) === String(activeBranchId));
    if (!hasCurrent) {
      setActiveBranchId(String(branches[0].Branch_Id));
    }
  }, [branches, activeBranchId]);

  const activeBranch = useMemo(
    () => branches.find((b) => String(b.Branch_Id) === String(activeBranchId)) || null,
    [branches, activeBranchId]
  );

  const activeBranchCoverageRows = useMemo(
    () => coverageAll
      .filter((r) => String(r.Branch_Id) === String(activeBranchId))
      .sort((a, b) => {
        const p = String(a.Province_Name || "").localeCompare(String(b.Province_Name || ""), "th");
        if (p !== 0) return p;
        return String(a.District_Name || "").localeCompare(String(b.District_Name || ""), "th");
      }),
    [coverageAll, activeBranchId]
  );

  useEffect(() => {
    if (!activeBranch) {
      setActiveBranchForm({ Branch_Name: "", Branch_Address: "" });
      return;
    }
    setActiveBranchForm({
      Branch_Name: activeBranch.Branch_Name || "",
      Branch_Address: getBranchAddressRaw(activeBranch),
    });
  }, [activeBranch]);

  const totalBranchPages = Math.max(1, Math.ceil((branches?.length || 0) / branchPerPage));
  const safeBranchPage = Math.min(branchPage, totalBranchPages);
  const pagedBranches = useMemo(() => {
    const start = (safeBranchPage - 1) * branchPerPage;
    return (branches || []).slice(start, start + branchPerPage);
  }, [branches, safeBranchPage, branchPerPage]);

  useEffect(() => {
    setBranchPage(1);
  }, [branchPerPage]);

  useEffect(() => {
    if (branchPage > totalBranchPages) setBranchPage(totalBranchPages);
  }, [branchPage, totalBranchPages]);

  const openBranchEdit = (row) => {
    if (row) {
      setBranchForm({
        Branch_Code: row.Branch_Code || "",
        Branch_Name: row.Branch_Name || "",
        Branch_Address: getBranchAddressRaw(row),
        Sort_Order: row.Sort_Order ?? "",
        Is_Active: row.Is_Active || "Y",
      });
      setBranchDialog({ open: true, row });
    } else {
      setBranchForm({
        Branch_Code: "",
        Branch_Name: "",
        Branch_Address: "",
        Sort_Order: "",
        Is_Active: "Y",
      });
      setBranchDialog({ open: true, row: null });
    }
  };

  const saveBranch = async () => {
    try {
      const body = {
        branch_name: branchForm.Branch_Name,
        branch_code: branchForm.Branch_Code || null,
        branch_address: branchForm.Branch_Address || null,
        sort_order: branchForm.Sort_Order === "" ? null : Number(branchForm.Sort_Order),
        is_active: branchForm.Is_Active,
      };
      if (branchDialog.row) {
        await Axios.put(`${apiApp()}/org/branches/${branchDialog.row.Branch_Id}`, body, authCfg());
      } else {
        await Axios.post(`${apiApp()}/org/branches`, body, authCfg());
      }
      setBranchDialog({ open: false, row: null });
      loadBranches();
    } catch (e) {
      getAlert("FAILED", e.response?.data?.message || e.message);
    }
  };

  const loadBranchContacts = useCallback(async (branchId) => {
    if (!branchId) { setBranchContacts([]); return; }
    try {
      setLoadingContacts(true);
      const res = await Axios.get(`${apiApp()}/org/branches/${branchId}/contacts`, authCfg());
      setBranchContacts(res.data?.results || []);
    } catch { setBranchContacts([]); }
    finally { setLoadingContacts(false); }
  }, []);

  const openContactDialog = (row = null) => {
    setContactForm(row ? { Contact_Name: row.Contact_Name || "", Contact_Tel: row.Contact_Tel || "" } : { Contact_Name: "", Contact_Tel: "" });
    setContactDialog({ open: true, row });
  };

  const saveContact = async () => {
    if (!activeBranchId) return;
    try {
      const body = { contact_name: contactForm.Contact_Name, contact_tel: contactForm.Contact_Tel };
      if (contactDialog.row) {
        await Axios.put(`${apiApp()}/org/branches/${activeBranchId}/contacts/${contactDialog.row.Contact_Id}`, body, authCfg());
      } else {
        await Axios.post(`${apiApp()}/org/branches/${activeBranchId}/contacts`, body, authCfg());
      }
      setContactDialog({ open: false, row: null });
      loadBranchContacts(activeBranchId);
      loadAllBranchContacts();
    } catch (e) { getAlert("FAILED", e.response?.data?.message || e.message); }
  };

  const deleteContact = async (contactId) => {
    const result = await Swal.fire({
      icon: "warning", title: "ยืนยันการลบ", text: "ลบผู้ติดต่อนี้ใช่หรือไม่?",
      showCancelButton: true, confirmButtonText: "ลบ", cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#E13F57", cancelButtonColor: "#6b7280",
    });
    if (!result.isConfirmed) return;
    try {
      await Axios.delete(`${apiApp()}/org/branches/${activeBranchId}/contacts/${contactId}`, authCfg());
      loadBranchContacts(activeBranchId);
      loadAllBranchContacts();
    } catch (e) { getAlert("FAILED", e.response?.data?.message || e.message); }
  };

  const saveActiveBranchInline = async () => {
    if (!activeBranch?.Branch_Id) return;
    try {
      const body = {
        branch_name: activeBranchForm.Branch_Name || activeBranch.Branch_Name,
        branch_code: activeBranch.Branch_Code || null,
        sort_order: activeBranch.Sort_Order ?? null,
        is_active: activeBranch.Is_Active || "Y",
        branch_address: activeBranchForm.Branch_Address || null,
      };
      await Axios.put(`${apiApp()}/org/branches/${activeBranch.Branch_Id}`, body, authCfg());
      getAlert("OK", "บันทึกสาขาแล้ว");
      await loadBranches();
      setActiveBranchId(String(activeBranch.Branch_Id));
    } catch (e) {
      getAlert("FAILED", e.response?.data?.message || e.message);
    }
  };

  useEffect(() => {
    setBranchRightTab("coverage");
    loadBranchContacts(activeBranchId);
  }, [activeBranchId, loadBranchContacts]);

  const toggleCovPendingP = (provinceId) => {
    const bid = targetCovBranchId;
    if (!bid) return;
    const pid = Number(provinceId);
    const rowP = findProvincePRow(coverageAll, pid);
    if (rowP && Number(rowP.Branch_Id) === bid) return;
    if (rowP && Number(rowP.Branch_Id) !== bid) return;
    if (!rowP && hasAnyDistrictInProvince(coverageAll, pid)) return;
    setCovPendingP((prev) => {
      if (prev.includes(pid)) return prev.filter((x) => x !== pid);
      return [...prev, pid];
    });
  };

  const toggleCovPendingD = (provinceId, districtId) => {
    const bid = targetCovBranchId;
    if (!bid) return;
    const pid = Number(provinceId);
    const did = Number(districtId);
    const key = `${pid}:${did}`;
    const rowD = findDistrictDRow(coverageAll, pid, did);
    if (rowD && Number(rowD.Branch_Id) === bid) return;
    if (rowD) return;
    if (findProvincePRow(coverageAll, pid)) return;
    setCovPendingD((prev) => {
      if (prev.includes(key)) return prev.filter((x) => x !== key);
      return [...prev, key];
    });
  };

  const saveCoverageBatch = async () => {
    try {
      const bid = targetCovBranchId;
      if (!bid) {
        getAlert("FAILED", "เลือกสาขาจาก dropdown ด้านบนก่อน (หรือสร้างสาขา)");
        return;
      }
      const items = [];
      if (covForm.Coverage_Level === "P") {
        covPendingP.forEach((pid) =>
          items.push({ coverage_level: "P", province_id: pid, district_id: null })
        );
      } else {
        covPendingD.forEach((key) => {
          const [p, d] = key.split(":").map(Number);
          items.push({ coverage_level: "D", province_id: p, district_id: d });
        });
      }
      if (!items.length) {
        getAlert("FAILED", "เลือกอำเภออย่างน้อย 1 อำเภอ");
        return;
      }
      if (covEditRow?.Coverage_Id) {
        await Axios.delete(`${apiApp()}/org/coverage/${covEditRow.Coverage_Id}`, authCfg());
      }
      for (const t of items) {
        await Axios.post(
          `${apiApp()}/org/coverage`,
          {
            branch_id: bid,
            coverage_level: t.coverage_level,
            province_id: t.province_id,
            district_id: t.district_id,
          },
          authCfg()
        );
      }
      getAlert("OK", covEditRow ? "แก้ไขพื้นที่ครอบคลุมแล้ว" : `เพิ่มพื้นที่ครอบคลุม ${items.length} รายการแล้ว`);
      setCovDialog(false);
      setCovEditRow(null);
      setCovPendingP([]);
      setCovPendingD([]);
      setCovForm({ Coverage_Level: "D", Province_Id: "", District_Id: "" });
      loadCoverage();
    } catch (e) {
      getAlert("FAILED", e.response?.data?.message || e.message);
    }
  };

  const deleteCoverageRow = async (id) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "ยืนยันการลบ",
      text: "คุณต้องการลบพื้นที่นี้ใช่หรือไม่?",
      showCancelButton: true,
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#E13F57",
      cancelButtonColor: "#6b7280",
    });
    if (!result.isConfirmed) return;
    try {
      await Axios.delete(`${apiApp()}/org/coverage/${id}`, authCfg());
      loadCoverage();
    } catch (e) {
      getAlert("FAILED", e.response?.data?.message || e.message);
    }
  };

  const openCoverageEdit = (row) => {
    if (!row) return;
    const pid = String(row.Province_Id || "");
    const did = String(row.District_Id || "");
    setCovBranchId(String(row.Branch_Id || ""));
    setCovEditRow(row);
    // โหมดนี้ต้องมีแค่ "อำเภอ" เท่านั้น
    setCovPendingP([]);
    if (pid && did && row.Coverage_Level === "D") {
      setCovPendingD([`${Number(pid)}:${Number(did)}`]);
    } else if (pid && row.Coverage_Level === "P") {
      // ถ้าแก้รายการระดับจังหวัดเดิม ให้โหลดอำเภอทั้งหมดของจังหวัดนั้นมาเลือกเป็นชุด
      setCovPendingD([]);
      fetchDistricts(pid)
        .then((ds) => {
          const keys = (Array.isArray(ds) ? ds : [])
            .map((d) => `${pid}:${Number(d.Id ?? d.DistrictId ?? d.District_Id ?? "")}`)
            .filter((k) => !k.endsWith(":NaN") && !k.endsWith(":"));
          setCovPendingD(keys);
        })
        .catch(() => setCovPendingD([]));
    } else {
      setCovPendingD([]);
    }
    setCovForm({
      Coverage_Level: "D",
      Province_Id: pid,
      District_Id: did,
    });
    fetchProvinces().then(setProvinces).catch(() => setProvinces([]));
    if (pid) fetchDistricts(pid).then(setDistricts).catch(() => setDistricts([]));
    setCovDialog(true);
  };

  const openRole = (row) => {
    if (row) {
      setRoleForm({
        Role_Code: row.Role_Code || "",
        Role_Name: row.Role_Name || "",
        Is_Active: row.Is_Active || "Y",
      });
      setRoleDialog({ open: true, row });
    } else {
      setRoleForm({ Role_Code: "", Role_Name: "", Is_Active: "Y" });
      setRoleDialog({ open: true, row: null });
    }
  };

  const saveRole = async () => {
    try {
      const body = {
        role_name: roleForm.Role_Name,
        role_code: roleForm.Role_Code || null,
        is_active: roleForm.Is_Active,
      };
      if (roleDialog.row) {
        await Axios.put(`${apiApp()}/org/comp-roles/${roleDialog.row.Role_Id}`, body, authCfg());
        getAlert("OK", "บันทึกบทบาทแล้ว");
      } else {
        await Axios.post(`${apiApp()}/org/comp-roles`, body, authCfg());
        getAlert("OK", "เพิ่มบทบาทแล้ว");
      }
      setRoleDialog({ open: false, row: null });
      loadRoles();
    } catch (e) {
      getAlert("FAILED", e.response?.data?.message || e.message);
    }
  };

  const openPerm = async (role) => {
    setPermDialog({ open: true, role });
    try {
      const { data } = await Axios.get(
        `${apiApp()}/org/comp-roles/${role.Role_Id}/permissions`,
        authCfg()
      );
      setPermKeysSet(new Set(data.keys || []));
    } catch (e) {
      setPermKeysSet(new Set());
      getAlert("FAILED", e.response?.data?.message || e.message);
    }
  };

  const togglePermKey = (key, checked) => {
    setPermKeysSet((prev) => {
      const n = new Set(prev);
      if (checked) n.add(key);
      else n.delete(key);
      return n;
    });
  };

  const pageCrudKeys = (p) => PERMISSION_CRUD.map(({ field }) => p[field]);

  const isPageFullyChecked = (p) => pageCrudKeys(p).every((k) => permKeysSet.has(k));

  const togglePageAll = (p, checked) => {
    const keys = pageCrudKeys(p);
    setPermKeysSet((prev) => {
      const n = new Set(prev);
      keys.forEach((k) => {
        if (checked) n.add(k);
        else n.delete(k);
      });
      return n;
    });
  };

  const toggleSectionAll = (section, checked) => {
    const keys = section.pages.flatMap((p) => pageCrudKeys(p));
    setPermKeysSet((prev) => {
      const n = new Set(prev);
      keys.forEach((k) => {
        if (checked) n.add(k);
        else n.delete(k);
      });
      return n;
    });
  };

  const savePerm = async () => {
    const targetRoleId = permDialog.role?.Role_Id || activeRoleId;
    if (!targetRoleId) {
      getAlert("FAILED", "กรุณาเลือกบทบาทก่อนบันทึกสิทธิ์");
      return;
    }
    try {
      const keys = Array.from(permKeysSet).sort();
      await Axios.put(
        `${apiApp()}/org/comp-roles/${targetRoleId}/permissions`,
        { keys },
        authCfg()
      );
      getAlert("OK", "บันทึกสิทธิ์แล้ว");
      if (permDialog.open) {
        setPermDialog({ open: false, role: null });
      }
    } catch (e) {
      getAlert("FAILED", e.response?.data?.message || e.message);
    }
  };

  const extraPermissionKeys = Array.from(permKeysSet)
    .filter((k) => !MATRIX_KEY_SET.has(k))
    .sort();

  const loadUserBinding = async (emp) => {
    if (!emp?.Emp_Id) return;
    try {
      const { data } = await Axios.get(`${apiApp()}/org/users/${emp.Emp_Id}/comp-role`, authCfg());
      setUserRoleRow(data.result);
      setRolePick(data.result ? String(data.result.Role_Id) : "");
      const { data: b } = await Axios.get(`${apiApp()}/org/users/${emp.Emp_Id}/branches`, authCfg());
      setUserBranches(b.results || []);
    } catch (e) {
      getAlert("FAILED", e.response?.data?.message || e.message);
    }
  };

  useEffect(() => {
    if (tab === 3 && selectedEmp) loadUserBinding(selectedEmp);
  }, [selectedEmp, tab]);

  const saveUserRole = async () => {
    if (!selectedEmp || !rolePick) {
      getAlert("FAILED", "เลือกพนักงานและบทบาท");
      return;
    }
    try {
      await Axios.put(
        `${apiApp()}/org/users/${selectedEmp.Emp_Id}/comp-role`,
        { role_id: Number(rolePick) },
        authCfg()
      );
      getAlert("OK", "กำหนดบทบาทแล้ว");
      loadUserBinding(selectedEmp);
    } catch (e) {
      getAlert("FAILED", e.response?.data?.message || e.message);
    }
  };

  const clearUserRole = async () => {
    if (!selectedEmp) return;
    if (!window.confirm("ลบบทบาทและการผูกสาขาของพนักงานคนนี้ในบริษัทนี้?")) return;
    try {
      await Axios.delete(`${apiApp()}/org/users/${selectedEmp.Emp_Id}/comp-role`, authCfg());
      getAlert("OK", "ลบแล้ว");
      setUserRoleRow(null);
      setRolePick("");
      setUserBranches([]);
    } catch (e) {
      getAlert("FAILED", e.response?.data?.message || e.message);
    }
  };

  const addUserBranch = async () => {
    if (!selectedEmp || !branchPick) {
      getAlert("FAILED", "เลือกพนักงานและสาขา");
      return;
    }
    try {
      await Axios.post(
        `${apiApp()}/org/users/${selectedEmp.Emp_Id}/branches`,
        { branch_id: Number(branchPick), is_default: branchDefault },
        authCfg()
      );
      getAlert("OK", "ผูกสาขาแล้ว");
      loadUserBinding(selectedEmp);
    } catch (e) {
      getAlert("FAILED", e.response?.data?.message || e.message);
    }
  };

  const removeUserBranch = async (bid) => {
    if (!selectedEmp) return;
    try {
      await Axios.delete(`${apiApp()}/org/users/${selectedEmp.Emp_Id}/branches/${bid}`, authCfg());
      getAlert("OK", "ถอดสาขาแล้ว");
      loadUserBinding(selectedEmp);
    } catch (e) {
      getAlert("FAILED", e.response?.data?.message || e.message);
    }
  };

  const activeRole = useMemo(
    () => roles.find((r) => String(r.Role_Id) === String(activeRoleId)) || null,
    [roles, activeRoleId]
  );

  const assignEmpToRole = async () => {
    if (!assignEmpPick || !activeRoleId) return;
    try {
      await Axios.put(
        `${apiApp()}/org/users/${assignEmpPick}/comp-role`,
        { role_id: Number(activeRoleId) },
        authCfg()
      );
      getAlert("OK", "กำหนดบทบาทแล้ว");
      setAssignEmpDialog(false);
      setAssignEmpPick("");
      loadRoleEmployees(activeRoleId);
    } catch (e) {
      getAlert("FAILED", e.response?.data?.message || e.message);
    }
  };

  const removeEmpFromRole = async (empId) => {
    if (!window.confirm("ถอดพนักงานออกจากบทบาทนี้?")) return;
    try {
      await Axios.delete(`${apiApp()}/org/users/${empId}/comp-role`, authCfg());
      getAlert("OK", "ถอดออกแล้ว");
      loadRoleEmployees(activeRoleId);
    } catch (e) {
      getAlert("FAILED", e.response?.data?.message || e.message);
    }
  };

  const editBtn = (onClick) => (
    <button
      type="button"
      onClick={(ev) => {
        ev.stopPropagation();
        onClick();
      }}
      className="btn btn-sm"
      title="แก้ไข"
      style={{
        width: "32px",
        height: "32px",
        borderRadius: "8px",
        border: "none",
        backgroundColor: "#fff3e0",
        color: THEME_COLOR,
        transition: "all 0.2s ease",
      }}
      onMouseOver={(ev) => {
        ev.currentTarget.style.backgroundColor = THEME_COLOR;
        ev.currentTarget.style.color = "#fff";
      }}
      onMouseOut={(ev) => {
        ev.currentTarget.style.backgroundColor = "#fff3e0";
        ev.currentTarget.style.color = THEME_COLOR;
      }}
    >
      <i className="fas fa-edit" />
    </button>
  );

  const primaryBtnStyle = {
    backgroundColor: THEME_COLOR,
    borderRadius: "10px",
    padding: "10px 24px",
    fontWeight: "500",
    border: "none",
    boxShadow: "0 2px 8px rgba(225, 63, 87, 0.2)",
  };

  return (
    <div
      className={`Creditor org-access-page ${mode === "roles" ? "roles-mode" : "branches-mode"}`}
      style={{ height: "100vh", overflow: "hidden" }}
    >
      <div className="wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="container-fluid">
            <div className="page-inner" style={{ marginTop: "56px", height: "calc(100vh - 56px)", overflow: "hidden" }}>
              <Topbar breadcrumbs={[]} />

              <style>
                {`
                  .org-access-page .main-panel {
                    background: #f5f7fb;
                    height: 100vh;
                    overflow: hidden;
                  }
                  .org-access-page .main-panel > .container-fluid {
                    width: 100% !important;
                    max-width: none !important;
                    padding-right: 2px !important;
                    padding-left: 2px !important;
                    height: 100%;
                    overflow: hidden;
                  }
                  .org-access-page .container,
                  .org-access-page .page-inner {
                    max-width: 100%;
                    width: 100% !important;
                  }
                  .org-access-page .page-inner {
                    padding-left: 0 !important;
                    padding-right: 0 !important;
                  }
                  .org-access-page .org-access-tab-panel {
                    width: 100% !important;
                    height: 100%;
                    padding: 0 6px;
                  }
                  .org-access-page .org-branch-layout {
                    width: 100%;
                    display: flex;
                    gap: 14px;
                    align-items: stretch;
                    height: 100%;
                    justify-content: flex-start;
                  }
                  .org-access-page .org-branch-left,
                  .org-access-page .org-branch-right {
                    min-width: 0;
                    width: 100%;
                  }
                  .org-access-page .org-branch-left {
                    flex: 0 0 500px;
                    max-width: 500px;
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                  }
                  .org-access-page.roles-mode .org-branch-left {
                    flex: 0 0 300px;
                    max-width: 300px;
                  }
                  .org-access-page .branch-left-stack {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    min-height: 0;
                    gap: 12px;
                  }
                  .org-access-page .branch-left-head-card {
                    flex: 0 0 auto;
                  }
                  .org-access-page .branch-left-list-card {
                    flex: 1 1 auto;
                    min-height: 0;
                    display: flex;
                    flex-direction: column;
                  }
                  .org-access-page .org-branch-right {
                    flex: 1 1 0;
                    max-width: none;
                    display: flex;
                    min-width: 0;
                    align-self: stretch;
                  }
                  .org-access-page .org-branch-right > .branch-right-panel {
                    flex: 1 1 auto;
                    width: 100% !important;
                    max-width: none !important;
                  }
                  @media (max-width: 991.98px) {
                    .org-access-page .org-branch-layout {
                      display: block;
                    }
                    .org-access-page .org-branch-left,
                    .org-access-page .org-branch-right {
                      max-width: 100%;
                    }
                  }
                  .org-access-page .org-card {
                    border: 1px solid #eef1f6 !important;
                    border-radius: 14px !important;
                    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.05) !important;
                    overflow: hidden;
                    background: #fff;
                  }
                  .org-access-page .org-toolbar {
                    background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%);
                  }
                  .org-access-page .nav-pills .nav-link {
                    border: 1px solid #e6e8ee;
                    background: #fff;
                    color: #374151;
                    min-width: 118px;
                  }
                  .org-access-page .nav-pills .nav-link.active {
                    box-shadow: 0 4px 12px rgba(225, 63, 87, 0.24);
                  }
                  .org-access-page .table > :not(caption) > * > * {
                    vertical-align: middle;
                  }
                  .org-access-page .table-hover tbody tr:hover {
                    background: #fff8fa;
                  }
                  .org-access-page .org-table-head th {
                    font-size: 13px;
                    letter-spacing: 0.2px;
                  }
                  .org-access-page thead th {
                    color: #fff !important;
                  }
                  .org-access-page .branch-left-panel,
                  .org-access-page .branch-right-panel {
                    border: 1px solid #e9edf3;
                    border-radius: 12px;
                    background: #fff;
                  }
                  .org-access-page .branch-list-scroll {
                    max-height: none;
                    overflow: auto;
                    padding-right: 4px;
                    width: 100%;
                    flex: 1 1 auto;
                    min-height: 0;
                  }
                  .org-access-page .branch-tile {
                    border: 1px solid #e8ebf0;
                    border-radius: 10px;
                    padding: 16px;
                    margin-bottom: 12px;
                    background: #fff;
                    width: 100%;
                    max-width: none;
                    box-sizing: border-box;
                    min-height: 165px;
                    display: flex;
                    flex-direction: column;
                  }
                  .org-access-page .branch-tile.active {
                    border-color: #e8a3ad;
                    box-shadow: inset 0 0 0 1px #f5c6cd;
                    background: #fffafb;
                  }
                  .org-access-page .branch-address-text {
                    color: #000 !important;
                  }
                  .org-access-page .branch-mini-tab {
                    font-size: 16px;
                    border: none;
                    background: transparent;
                    padding: 6px 0;
                    margin-right: 20px;
                    color: #6b7280;
                    border-bottom: 2px solid transparent;
                  }
                  .org-access-page .branch-mini-tab.active {
                    color: #111827;
                    border-bottom-color: #E13F57;
                    font-weight: 600;
                  }
                  .org-access-page .branch-blue-btn {
                    background: #2f6df6;
                    border: 1px solid #2f6df6;
                    color: #fff;
                    border-radius: 20px;
                    font-size: 14px;
                    font-weight: 600;
                    padding: 6px 16px;
                    min-width: 116px;
                    white-space: nowrap;
                  }
                  .org-access-page .branch-blue-btn:hover {
                    background: #2558c7;
                    border-color: #2558c7;
                    color: #fff;
                  }
                  .org-access-page .branch-red-head {
                    background: #E13F57 !important;
                    color: #fff !important;
                    font-weight: 600;
                  }
                  .org-access-page .branch-right-table-wrap {
                    border: 1px solid #e6ebf2;
                    border-radius: 14px;
                    overflow: hidden;
                    background: #fff;
                    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
                    width: 100%;
                    max-width: none;
                  }
                  .org-access-page .branch-right-table {
                    border-collapse: separate;
                    border-spacing: 0;
                    margin-bottom: 0;
                  }
                  .org-access-page .branch-right-table thead th:first-child {
                    border-top-left-radius: 14px;
                  }
                  .org-access-page .branch-right-table thead th:last-child {
                    border-top-right-radius: 14px;
                  }
                  .org-access-page .branch-right-table tbody tr:last-child td:first-child {
                    border-bottom-left-radius: 14px;
                  }
                  .org-access-page .branch-right-table tbody tr:last-child td:last-child {
                    border-bottom-right-radius: 14px;
                  }
                  .org-access-page .branch-right-table tbody tr td {
                    border-bottom: none;
                  }
                  .org-access-page .branch-right-table > :not(caption) > * > * {
                    border-bottom-width: 0 !important;
                    border-color: transparent !important;
                  }
                  .org-access-page .branch-right-table tbody tr:nth-child(even) td {
                    background: #f5f6f8;
                  }
                  .org-access-page .branch-right-table tbody tr:nth-child(odd) td {
                    background: #ffffff;
                  }
                  .org-access-page .branch-right-table th,
                  .org-access-page .branch-right-table td {
                    border-left: none !important;
                    border-right: none !important;
                    text-align: center !important;
                    font-size: 14px !important;
                  }
                  .org-access-page .branch-right-table tbody tr:last-child td {
                    border-bottom: none;
                  }
                  .org-access-page .branch-right-table tbody tr:hover td {
                    background: #fff7f9;
                  }
                  .org-access-page .branch-contact-row {
                    display: grid;
                    grid-template-columns: minmax(0, 1fr) auto;
                    gap: 8px;
                    align-items: start;
                  }
                  .org-access-page .branch-right-panel input.form-control,
                  .org-access-page .branch-right-panel select.form-select {
                    text-align: left !important;
                  }
                  .org-access-page .branch-right-panel .form-label {
                    display: block;
                    width: 100%;
                    text-align: left !important;
                  }
                  .org-access-page .cov-modal-shell .form-label {
                    font-weight: 600;
                    color: #334155;
                  }
                  .org-access-page .cov-modal-shell .form-select {
                    border-radius: 10px;
                    border: 1px solid #d9dee8;
                    min-height: 40px;
                  }
                  .org-access-page .cov-mode-toggle {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 999px;
                    padding: 4px;
                  }
                  .org-access-page .cov-mode-btn {
                    border: none;
                    background: transparent;
                    border-radius: 999px;
                    padding: 6px 14px;
                    font-size: 13px;
                    color: #475569;
                    white-space: nowrap;
                  }
                  .org-access-page .cov-mode-btn.active {
                    background: #E13F57;
                    color: #fff;
                    box-shadow: 0 4px 10px rgba(225, 63, 87, 0.25);
                  }
                  .org-access-page .cov-list-wrap {
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    background: #f8fafc;
                    max-height: min(52vh, 440px);
                    overflow-y: auto;
                    overflow-x: hidden;
                    padding: 12px;
                  }
                  .org-access-page .cov-list-wrap.district {
                    max-height: min(50vh, 400px);
                  }
                  .org-access-page .cov-counter {
                    font-size: 12px;
                    color: #64748b;
                    margin-top: 6px;
                  }
                `}
              </style>

              {migrationMissing && (
                <div className="alert alert-warning" role="alert">
                  ยังไม่มีตาราง Phase 1 — รันไฟล์{" "}
                  <code>database/migrations/001_phase1_branch_roles.sql</code> บน SQL Server ก่อน
                </div>
              )}

              {/* <p className="text-muted small mb-3">
                ใช้ได้เฉพาะ Emp_Role 1 หรือ 2 — กำหนดสาขา พื้นที่ครอบคลุม บทบาท และการผูกพนักงาน
              </p> */}

              {mode === "roles" && (
                <div className="org-branch-layout">
                  {/* ซ้าย — รายการบทบาท */}
                  <div className="org-branch-left">
                    <div className="branch-left-stack">
                      <div
                        className="branch-left-panel p-0 d-flex flex-column"
                        style={{
                          width: "100%",
                          minHeight: "100%",
                          borderRadius: "14px",
                          overflow: "hidden",
                          border: `1px solid ${THEME_COLOR}`,
                        }}
                      >
                        <div
                          className="d-flex align-items-center justify-content-between"
                          style={{
                            background: "transparent",
                            color: THEME_COLOR,
                            fontWeight: 700,
                            fontSize: "18px",
                            padding: "10px 12px",
                            width: "100%",
                            lineHeight: 1.2,
                          }}
                        >
                          <span>กลุ่มสิทธิ์</span>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                              border: `1px solid ${THEME_COLOR}`,
                              color: THEME_COLOR,
                              borderRadius: "8px",
                              fontWeight: 600,
                              padding: "2px 10px",
                              background: "#fff",
                            }}
                            onClick={() => openRole(null)}
                          >
                            <i className="fa fa-plus me-1" />
                            เพิ่ม
                          </button>
                        </div>
                        <div className="branch-list-scroll" style={{ width: "100%", padding: 0, maxHeight: "unset" }}>
                          {loadingRoles ? (
                            <div className="text-muted small py-3 px-3">กำลังโหลดบทบาท...</div>
                          ) : roles.length ? (
                            roles.map((r) => {
                              const isActiveRole = String(activeRoleId) === String(r.Role_Id);
                              return (
                                <div
                                  key={r.Role_Id}
                                  className="d-flex align-items-center gap-2"
                                  style={{
                                    width: "100%",
                                    padding: "11px 14px",
                                    borderBottom: "1px solid #e5e7eb",
                                    background: isActiveRole ? THEME_COLOR : "#ffffff",
                                    color: isActiveRole ? "#ffffff" : "#1f2937",
                                    cursor: "pointer",
                                    fontSize: "28px",
                                    fontWeight: 600,
                                    lineHeight: 1.25,
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => setActiveRoleId(String(r.Role_Id))}
                                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setActiveRoleId(String(r.Role_Id))}
                                >
                                  <i className="far fa-shield-alt" style={{ fontSize: "18px", width: 18 }} />
                                  <span className="fw-semibold" style={{ fontSize: "16px" }}>
                                    {r.Role_Name}
                                  </span>
                                </div>
                              );
                            })
                          ) : (
                            <div className="text-center text-muted py-4">ยังไม่มีกลุ่มสิทธิ์</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ขวา — สิทธิ์บทบาท + พนักงานในบทบาทที่เลือก */}
                  <div className="org-branch-right">
                    <div className="branch-right-panel p-3 d-flex flex-column" style={{ minHeight: "100%", height: "100%" }}>
                      <div className="d-flex align-items-center mb-3 w-100">
                        <h4 className="fw-semibold mb-0" style={{ fontSize: "2rem", lineHeight: 1.2 }}>
                          {activeRole ? `สิทธิ์กลุ่ม: ${activeRole.Role_Name}` : "เลือกกลุ่มสิทธิ์"}
                        </h4>
                        <div className="d-flex align-items-center ms-auto justify-content-end">
                          {activeRoleId && (
                            <button
                              type="button"
                              className="btn btn-sm branch-blue-btn"
                              onClick={savePerm}
                            >
                              <i className="fa fa-save me-1" />
                              บันทึกสิทธิ์
                            </button>
                          )}
                        </div>
                      </div>

                      {!activeRoleId ? (
                        <div className="text-center text-muted py-5">กรุณาเลือกบทบาทจากด้านซ้าย</div>
                      ) : (
                        <>
                          <div
                            className="table-responsive border rounded-3 shadow-sm mb-0 flex-grow-1"
                            style={{ minHeight: 0, height: "100%", borderRadius: "12px", overflowX: "auto", overflowY: "auto" }}
                          >
                            <table
                              className="table table-sm table-bordered mb-0 align-middle"
                              style={{ fontSize: "13px", borderCollapse: "separate", borderSpacing: 0 }}
                            >
                              <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                <tr style={{ backgroundColor: THEME_COLOR, color: "#ffffff" }}>
                                  <th className="py-2 ps-3" style={{ borderTopLeftRadius: "12px" }}>เมนู</th>
                                  {PERMISSION_CRUD.map((c) => (
                                    <th key={c.field} className="text-center py-2" style={{ width: "74px" }}>
                                      {c.label}
                                    </th>
                                  ))}
                                  <th className="text-center py-2" style={{ width: "88px", borderTopRightRadius: "12px" }}>
                                    ทั้งแถว
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {PERMISSION_MATRIX.flatMap((section) => {
                                  const sectionRows = section.pages.map((p) => (
                                    <tr key={p.id}>
                                      <td className="ps-3">
                                        <span className="fw-medium">{p.label}</span>
                                        {p.path ? (
                                          <span className="text-muted small d-block font-monospace">{p.path}</span>
                                        ) : null}
                                      </td>
                                      {PERMISSION_CRUD.map(({ field }) => (
                                        <td key={field} className="text-center">
                                          <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={permKeysSet.has(p[field])}
                                            onChange={(e) => togglePermKey(p[field], e.target.checked)}
                                            title={p[field]}
                                          />
                                        </td>
                                      ))}
                                      <td className="text-center">
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          checked={isPageFullyChecked(p)}
                                          onChange={(e) => togglePageAll(p, e.target.checked)}
                                          title="เลือก/ล้าง CRUD ทั้งหมดของหน้านี้"
                                        />
                                      </td>
                                    </tr>
                                  ));

                                  return [
                                    <tr key={`sec-${section.title}`} style={{ background: "rgba(225, 63, 87, 0.07)" }}>
                                      <td className="ps-3 fw-semibold" style={{ color: THEME_COLOR }} colSpan={6}>
                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                          <span>{section.title}</span>
                                          <div className="btn-group btn-group-sm" role="group">
                                            <button
                                              type="button"
                                              className="btn btn-outline-secondary"
                                              onClick={() => toggleSectionAll(section, true)}
                                            >
                                              เลือกครบหมวด
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-outline-secondary"
                                              onClick={() => toggleSectionAll(section, false)}
                                            >
                                              ล้างหมวด
                                            </button>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>,
                                    ...sectionRows,
                                  ];
                                })}
                              </tbody>
                            </table>
                          </div>

                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <style>
                {`
                  @keyframes org-access-tab-in {
                    from { opacity: 0.35; }
                    to { opacity: 1; }
                  }
                  .org-access-tab-panel {
                    animation: org-access-tab-in 0.22s ease-out;
                  }
                `}
              </style>
              {mode !== "roles" && <div className="org-access-tab-panel" key={TAB_KEYS[tab]}>
              {tab === 0 && (
                <>
                  <div className="org-branch-layout">
                    <div className="org-branch-left">
                      <div className="branch-left-stack">
                        <div className="branch-left-panel branch-left-head-card p-3" style={{ width: "100%" }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <h4
                              className="mb-0 fw-semibold text-start"
                              style={{ fontSize: "2rem", lineHeight: 1.2, textAlign: "left", width: "100%" }}
                            >
                              สาขาทั้งหมด
                            </h4>
                            <button
                              type="button"
                              className="btn btn-sm branch-blue-btn"
                              onClick={() => openBranchEdit(null)}
                            >
                              <i className="fa fa-plus me-1" />
                              สร้างสาขา
                            </button>
                          </div>
                        </div>
                        <div className="branch-left-panel branch-left-list-card p-3 d-flex flex-column" style={{ width: "100%" }}>
                          <div className="branch-list-scroll" style={{ width: "100%" }}>
                            {loadingBranches ? (
                              <div className="text-muted small py-2">กำลังโหลดรายการสาขา...</div>
                            ) : branches.length ? (
                              pagedBranches.map((b) => {
                                const isActive = String(activeBranchId) === String(b.Branch_Id);
                                const contacts = allBranchContactsMap[b.Branch_Id] || [];
                                return (
                                  <div
                                    key={b.Branch_Id}
                                    className={`branch-tile ${isActive ? "active" : ""}`}
                                    style={{ width: "100%", maxWidth: "100%", marginLeft: 0, marginRight: 0 }}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setActiveBranchId(String(b.Branch_Id))}
                                    onKeyDown={(e) =>
                                      (e.key === "Enter" || e.key === " ") &&
                                      setActiveBranchId(String(b.Branch_Id))
                                    }
                                  >
                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                      <div className="fw-semibold" style={{ color: THEME_COLOR, fontSize: "2rem", lineHeight: 1.2 }}>
                                        {`สาขา ${b.Branch_Name}`}
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-link p-0"
                                        style={{ color: THEME_COLOR }}
                                        onClick={(ev) => {
                                          ev.stopPropagation();
                                          openBranchEdit(b);
                                        }}
                                        title="แก้ไขสาขา"
                                      >
                                        <i className="fas fa-pen" />
                                      </button>
                                    </div>
                                    <div className="mb-2 branch-address-text" style={{ fontSize: "14px" }}>{getBranchAddress(b)}</div>
                                    <div className="fw-semibold mb-1" style={{ color: THEME_COLOR, fontSize: "16px", lineHeight: 1.2 }}>
                                      ผู้ติดต่อ
                                    </div>
                                    {contacts.map((c, idx) => (
                                      <div key={c.Contact_Id ?? idx} className="branch-contact-row" style={{ fontSize: "14px" }}>
                                        <span>{`คนที่ ${idx + 1} ${c.Contact_Name || ""}`}</span>
                                        <span>{c.Contact_Tel || ""}</span>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })
                            ) : (
                              <div className="text-center text-muted py-4">ยังไม่มีสาขา</div>
                            )}
                          </div>
                          {!loadingBranches && branches.length > 0 && (
                            <div
                              className="mt-2 pt-2"
                              style={{ borderTop: "1px solid #e9edf3", width: "100%", display: "block" }}
                            >
                              <Pagination
                                key={`branch-pagination-${safeBranchPage}-${branchPerPage}`}
                                totalItems={branches.length}
                                itemsPerPage={branchPerPage}
                                currentPage={safeBranchPage}
                                compact
                                showTotalItems={false}
                                onPageChange={(p) =>
                                  setBranchPage(Number.isFinite(Number(p)) ? Number(p) : 1)
                                }
                                onItemsPerPageChange={(val) => {
                                  setBranchPerPage(Number(val));
                                  setBranchPage(1);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="org-branch-right">
                      <div className="branch-right-panel p-3 d-flex flex-column" style={{ minHeight: "100%", height: "100%" }}>
                        <h4
                          className="fw-semibold mb-1 text-start"
                          style={{ fontSize: "2rem", lineHeight: 1.2, textAlign: "left", width: "100%" }}
                        >
                          {activeBranch ? `สาขา ${activeBranch.Branch_Name}` : "เลือกสาขา"}
                        </h4>
                        <div
                          className="d-flex align-items-center border-bottom mb-3"
                          style={{ width: "100%", justifyContent: "flex-start" }}
                        >
                          <button type="button" className={`branch-mini-tab ${branchRightTab === "coverage" ? "active" : ""}`} onClick={() => setBranchRightTab("coverage")}>ขอบเขตการดูแล</button>
                          <button type="button" className={`branch-mini-tab ${branchRightTab === "contacts" ? "active" : ""}`} onClick={() => setBranchRightTab("contacts")}>ข้อมูลผู้ติดต่อ</button>
                        </div>

                        {branchRightTab === "contacts" && (
                          <div className="w-100">
                            <div className="d-flex align-items-center justify-content-end mb-2">
                              <button
                                type="button"
                                className="btn btn-sm branch-blue-btn"
                                onClick={() => openContactDialog()}
                                disabled={!activeBranchId}
                              >
                                <i className="fa fa-plus me-1" />ผู้ติดต่อ
                              </button>
                            </div>
                            <div className="table-responsive branch-right-table-wrap">
                              <table className="table table-hover branch-right-table" style={{ fontSize: "14px" }}>
                                <thead className="branch-red-head">
                                  <tr>
                                    <th className="text-center" style={{ width: 48 }}>#</th>
                                    <th>ชื่อ</th>
                                    <th>เบอร์โทร</th>
                                    <th className="text-center" style={{ width: 80 }}>จัดการ</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {loadingContacts ? (
                                    <OrgTableSkeletonRows colSpan={4} rows={4} />
                                  ) : branchContacts.length ? (
                                    branchContacts.map((c, i) => (
                                      <tr key={c.Contact_Id}>
                                        <td className="text-center">{i + 1}</td>
                                        <td>{c.Contact_Name || "-"}</td>
                                        <td>{c.Contact_Tel || "-"}</td>
                                        <td className="text-center">
                                          <button type="button" className="btn btn-link p-0 me-2" style={{ color: THEME_COLOR }} onClick={() => openContactDialog(c)} title="แก้ไข">
                                            <i className="fas fa-pen" />
                                          </button>
                                          <button type="button" className="btn btn-link text-danger p-0" onClick={() => deleteContact(c.Contact_Id)} title="ลบ">
                                            <i className="fas fa-trash-alt" />
                                          </button>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr><td colSpan={4} className="text-center text-muted py-4">ยังไม่มีผู้ติดต่อ</td></tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {branchRightTab === "coverage" && <><div
                          className="d-flex align-items-center mb-2"
                          style={{ width: "100%", justifyContent: "flex-end" }}
                        >
                          <button
                            type="button"
                            className="btn btn-sm branch-blue-btn"
                            onClick={() => {
                              if (activeBranchId) setCovBranchId(String(activeBranchId));
                              setCovEditRow(null);
                              setCovPendingP([]);
                              setCovPendingD([]);
                              setCovForm({ Coverage_Level: "D", Province_Id: "", District_Id: "" });
                              fetchProvinces().then(setProvinces).catch(() => setProvinces([]));
                              setCovDialog(true);
                            }}
                            disabled={!activeBranchId}
                          >
                            <i className="fa fa-plus me-1" />
                            ขอบเขตการดูแล
                          </button>
                        </div>

                        <div className="table-responsive branch-right-table-wrap">
                          <table className="table table-hover branch-right-table" style={{ fontSize: "14px" }}>
                            <thead className="branch-red-head">
                              <tr>
                                <th className="text-center" style={{ width: 56 }}>#</th>
                                <th>อำเภอ</th>
                                <th>จังหวัด</th>
                                <th className="text-center" style={{ width: 110 }}>
                                  จัดการ
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {loadingCoverage ? (
                                <OrgTableSkeletonRows colSpan={4} rows={8} />
                              ) : activeBranchCoverageRows.length ? (
                                activeBranchCoverageRows.map((r, i) => (
                                  <tr key={r.Coverage_Id}>
                                    <td className="text-center">{i + 1}</td>
                                    <td>{r.District_Name || "ทั้งจังหวัด"}</td>
                                    <td>{r.Province_Name || "-"}</td>
                                    <td className="text-center">
                                      <button
                                        type="button"
                                        className="btn btn-link text-danger p-0"
                                        onClick={() => deleteCoverageRow(r.Coverage_Id)}
                                        title="ลบพื้นที่"
                                      >
                                        <i className="fas fa-trash-alt" />
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={4} className="text-center text-muted py-4">
                                    ยังไม่มีขอบเขตการดูแล
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        </>}

                      </div>
                    </div>
                  </div>
                </>
              )}

              {false && tab === 1 && (
                <>
                  <div
                    className="card shadow-sm mb-3 border-0 org-card org-toolbar"
                    style={{ borderRadius: "12px", overflow: "hidden" }}
                  >
                    <div className="card-body p-3">
                      <div className="row g-3 align-items-end">
                        <div className="col-md-4">
                          <label className="form-label small text-muted mb-1">สาขา (กรอง)</label>
                          <select
                            className="form-select"
                            style={{ borderRadius: "10px" }}
                            value={covBranchId}
                            onChange={(e) => setCovBranchId(e.target.value)}
                          >
                            <option value="">ทั้งหมด</option>
                            {branches.map((b) => (
                              <option key={b.Branch_Id} value={String(b.Branch_Id)}>
                                {b.Branch_Name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-4 d-flex gap-2">
                          <button
                            type="button"
                            className="btn text-white"
                            style={{ ...primaryBtnStyle, padding: "10px 20px" }}
                            onClick={() => {
                              setCovPendingP([]);
                              setCovPendingD([]);
                              setCovForm({ Coverage_Level: "D", Province_Id: "", District_Id: "" });
                              loadCoverage();
                              setCovDialog(true);
                            }}
                          >
                            <i className="fa fa-plus me-2" />
                            เพิ่มพื้นที่
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            style={{ borderRadius: "10px" }}
                            onClick={loadCoverage}
                            disabled={loadingCoverage}
                          >
                            รีเฟรช
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="card shadow-sm border-0 org-card"
                    style={{ borderRadius: "12px", overflow: "hidden" }}
                  >
                    <div className="card-body p-0">
                      <div className="table-responsive" style={orgTableScrollBoxStyle}>
                        <table className="table table-hover mb-0" style={{ fontSize: "14px" }}>
                          <thead className="org-table-head" style={tableHeaderStyle}>
                            <tr>
                              <th className="py-3" style={headerCellStyle}>
                                สาขา
                              </th>
                              <th className="py-3 text-center" style={headerCellStyle}>
                                ระดับ
                              </th>
                              <th className="py-3" style={headerCellStyle}>
                                จังหวัด
                              </th>
                              <th className="py-3" style={headerCellStyle}>
                                อำเภอ
                              </th>
                              <th className="py-3 text-center" style={{ ...headerCellStyle, width: "10%" }}>
                                ลบ
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingCoverage ? (
                              <OrgTableSkeletonRows colSpan={5} rows={10} />
                            ) : coverageRows.length ? (
                              coverageRows.map((r) => (
                                <tr key={r.Coverage_Id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                                  <td className="py-3">{branchName(r.Branch_Id)}</td>
                                  <td className="text-center py-3">
                                    {r.Coverage_Level === "P" ? "ทั้งจังหวัด" : "อำเภอ"}
                                  </td>
                                  <td className="py-3">{r.Province_Name || r.Province_Id}</td>
                                  <td className="py-3">{r.District_Name || r.District_Id || "—"}</td>
                                  <td className="text-center py-3">
                                    <button
                                      type="button"
                                      className="btn btn-link text-danger p-0"
                                      onClick={() => deleteCoverageRow(r.Coverage_Id)}
                                      title="ลบ"
                                    >
                                      <i className="fas fa-trash-alt" />
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={5} className="text-center text-muted py-5">
                                  ยังไม่มีข้อมูล
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {tab === 1 && (
                <>
                  <div
                    className="card shadow-sm mb-3 border-0 org-card org-toolbar"
                    style={{ borderRadius: "12px", overflow: "hidden" }}
                  >
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn text-white d-flex align-items-center gap-2"
                          style={primaryBtnStyle}
                          onClick={() => openRole(null)}
                        >
                          <i className="fa fa-plus" />
                          <span>สร้างบทบาทบริษัท</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div
                    className="card shadow-sm border-0 org-card"
                    style={{ borderRadius: "12px", overflow: "hidden" }}
                  >
                    <div className="card-body p-0">
                      <div className="table-responsive" style={orgTableScrollBoxStyle}>
                        <table className="table table-hover mb-0" style={{ fontSize: "14px" }}>
                          <thead className="org-table-head" style={tableHeaderStyle}>
                            <tr>
                              <th className="py-3" style={{ ...headerCellStyle, width: "18%" }}>
                                รหัส
                              </th>
                              <th className="py-3" style={headerCellStyle}>
                                ชื่อ
                              </th>
                              <th className="py-3 text-center" style={{ ...headerCellStyle, width: "12%" }}>
                                สถานะ
                              </th>
                              <th className="py-3 text-center" style={{ ...headerCellStyle, width: "22%" }}>
                                การทำงาน
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingRoles ? (
                              <OrgTableSkeletonRows colSpan={4} rows={10} />
                            ) : roles.length ? (
                              roles.map((r) => (
                                <tr key={r.Role_Id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                                  <td className="py-3">{r.Role_Code || "—"}</td>
                                  <td className="py-3">{r.Role_Name}</td>
                                  <td className="text-center py-3">
                                    {r.Is_Active === "Y" ? "ใช้งาน" : "ไม่ใช้งาน"}
                                  </td>
                                  <td className="text-center py-3">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary me-1"
                                      style={{ borderRadius: "8px" }}
                                      onClick={() => openPerm(r)}
                                    >
                                      สิทธิ์
                                    </button>
                                    {editBtn(() => openRole(r))}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={4} className="text-center text-muted py-5">
                                  ยังไม่มีบทบาท
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {tab === 2 && (
                <div className="card shadow-sm border-0 org-card" style={{ borderRadius: "12px" }}>
                  <div className="card-body p-4">
                    <h6 className="fw-semibold mb-3">ผูกพนักงานกับบทบาทและสาขา</h6>
                    <div className="row g-3 mb-3">
                      <div className="col-md-5">
                        <label className="form-label">ค้นหาพนักงาน</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            style={{ borderRadius: "10px 0 0 10px" }}
                            placeholder="ชื่อ / รหัส / ยูสเซอร์"
                            value={empInput}
                            onChange={(e) => setEmpInput(e.target.value)}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            style={{ borderRadius: "0 10px 10px 0" }}
                            onClick={() =>
                              fetchEmployees(empInput).then(setEmpOptions).catch(() => setEmpOptions([]))
                            }
                          >
                            ค้นหา
                          </button>
                        </div>
                      </div>
                      <div className="col-md-7">
                        <label className="form-label">เลือกพนักงาน</label>
                        <select
                          className="form-select"
                          style={{ borderRadius: "10px" }}
                          value={selectedEmp?.Emp_Id ?? ""}
                          onChange={(e) => {
                            const id = e.target.value;
                            const emp = empOptions.find((x) => String(x.Emp_Id) === id);
                            setSelectedEmp(emp || null);
                          }}
                        >
                          <option value="">— เลือกพนักงาน —</option>
                          {empOptions.map((emp) => (
                            <option key={emp.Emp_Id} value={emp.Emp_Id}>
                              {emp.Emp_Name}
                              {emp.Emp_Code ? ` (${emp.Emp_Code})` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {selectedEmp && (
                      <>
                        <div className="border rounded-3 p-3 mb-3 bg-light bg-opacity-50">
                          <p className="small text-muted mb-2">
                            บทบาทปัจจุบัน:{" "}
                            <strong>
                              {userRoleRow
                                ? `${userRoleRow.Role_Name} (#${userRoleRow.Role_Id})`
                                : "ยังไม่กำหนด (legacy)"}
                            </strong>
                          </p>
                          <div className="row g-2 align-items-end">
                            <div className="col-md-5">
                              <label className="form-label small">บทบาท</label>
                              <select
                                className="form-select"
                                style={{ borderRadius: "10px" }}
                                value={rolePick}
                                onChange={(e) => setRolePick(e.target.value)}
                              >
                                <option value="">— เลือก —</option>
                                {roles
                                  .filter((r) => r.Is_Active === "Y")
                                  .map((r) => (
                                    <option key={r.Role_Id} value={String(r.Role_Id)}>
                                      {r.Role_Name}
                                    </option>
                                  ))}
                              </select>
                            </div>
                            <div className="col-md-7 d-flex flex-wrap gap-2">
                              <button
                                type="button"
                                className="btn text-white"
                                style={{ ...primaryBtnStyle, padding: "8px 18px" }}
                                onClick={saveUserRole}
                              >
                                บันทึกบทบาท
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-warning"
                                style={{ borderRadius: "10px" }}
                                onClick={clearUserRole}
                              >
                                ลบบทบาท
                              </button>
                            </div>
                          </div>
                        </div>

                        <h6 className="fw-semibold mb-2">สาขาที่ผูก</h6>
                        <div className="table-responsive mb-3" style={{ maxWidth: 640 }}>
                          <table className="table table-sm table-bordered bg-white" style={{ fontSize: "14px" }}>
                            <thead style={tableHeaderStyle}>
                              <tr>
                                <th className="py-2" style={headerCellStyle}>
                                  สาขา
                                </th>
                                <th className="py-2 text-center" style={{ ...headerCellStyle, width: "100px" }}>
                                  ค่าเริ่มต้น
                                </th>
                                <th className="py-2 text-center" style={{ ...headerCellStyle, width: "60px" }}>
                                  ลบ
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {userBranches.length ? (
                                userBranches.map((ub) => (
                                  <tr key={ub.Branch_Id}>
                                    <td>{ub.Branch_Name}</td>
                                    <td className="text-center">{ub.Is_Default}</td>
                                    <td className="text-center">
                                      <button
                                        type="button"
                                        className="btn btn-link text-danger p-0 btn-sm"
                                        onClick={() => removeUserBranch(ub.Branch_Id)}
                                      >
                                        <i className="fas fa-trash-alt" />
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={3} className="text-center text-muted py-3">
                                    ยังไม่ผูกสาขา
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        <div className="row g-2 align-items-end">
                          <div className="col-md-5">
                            <label className="form-label small">เพิ่มสาขา</label>
                            <select
                              className="form-select"
                              style={{ borderRadius: "10px" }}
                              value={branchPick}
                              onChange={(e) => setBranchPick(e.target.value)}
                            >
                              <option value="">— เลือกสาขา —</option>
                              {branches
                                .filter((b) => b.Is_Active === "Y")
                                .map((b) => (
                                  <option key={b.Branch_Id} value={String(b.Branch_Id)}>
                                    {b.Branch_Name}
                                  </option>
                                ))}
                            </select>
                          </div>
                          <div className="col-md-4">
                            <div className="form-check mt-4">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="orgBranchDefault"
                                checked={branchDefault}
                                onChange={(e) => setBranchDefault(e.target.checked)}
                              />
                              <label className="form-check-label" htmlFor="orgBranchDefault">
                                ตั้งเป็นสาขาเริ่มต้น
                              </label>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <button
                              type="button"
                              className="btn btn-outline-primary w-100"
                              style={{ borderRadius: "10px", borderColor: THEME_COLOR, color: THEME_COLOR }}
                              onClick={addUserBranch}
                            >
                              ผูกสาขา
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
              </div>}
            </div>
          </div>
        </div>
      </div>

      <ModalShell
        show={branchDialog.open}
        title={branchDialog.row ? "แก้ไขสาขา" : "เพิ่มสาขา"}
        onClose={() => setBranchDialog({ open: false, row: null })}
        footer={
          <>
            <button
              type="button"
              className="btn btn-light"
              style={{ borderRadius: "10px" }}
              onClick={() => setBranchDialog({ open: false, row: null })}
            >
              ยกเลิก
            </button>
            <button type="button" className="btn text-white" style={primaryBtnStyle} onClick={saveBranch}>
              บันทึก
            </button>
          </>
        }
      >
        <div className="d-flex flex-column gap-3">
          <div className="row g-2 mx-0 w-100">
            <div className="col-3 px-1">
              <label className="form-label">ชื่อสาขา <span className="text-danger">*</span></label>
              <input
                className="form-control"
                style={{ borderRadius: "10px" }}
                value={branchForm.Branch_Name}
                onChange={(e) => setBranchForm((f) => ({ ...f, Branch_Name: e.target.value }))}
              />
            </div>
            <div className="col-9 px-1">
              <label className="form-label">ที่อยู่สาขา</label>
              <input
                className="form-control"
                style={{ borderRadius: "10px" }}
                value={branchForm.Branch_Address}
                onChange={(e) => setBranchForm((f) => ({ ...f, Branch_Address: e.target.value }))}
              />
            </div>
          </div>
        </div>
      </ModalShell>

      <ModalShell
        show={contactDialog.open}
        title={contactDialog.row ? "แก้ไขผู้ติดต่อ" : "เพิ่มผู้ติดต่อ"}
        onClose={() => setContactDialog({ open: false, row: null })}
        footer={
          <>
            <button type="button" className="btn btn-light" style={{ borderRadius: "10px" }} onClick={() => setContactDialog({ open: false, row: null })}>ยกเลิก</button>
            <button type="button" className="btn text-white" style={primaryBtnStyle} onClick={saveContact}>บันทึก</button>
          </>
        }
      >
        <div className="d-flex gap-3">
          <div style={{ flex: 1 }}>
            <label className="form-label">ชื่อผู้ติดต่อ</label>
            <input
              className="form-control"
              style={{ borderRadius: "10px" }}
              value={contactForm.Contact_Name}
              onChange={(e) => setContactForm((f) => ({ ...f, Contact_Name: e.target.value }))}
              placeholder="ชื่อ-นามสกุล"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label className="form-label">เบอร์โทร</label>
            <input
              className="form-control"
              style={{ borderRadius: "10px" }}
              value={contactForm.Contact_Tel}
              onChange={(e) => setContactForm((f) => ({ ...f, Contact_Tel: e.target.value }))}
              placeholder="0xx-xxx-xxxx"
            />
          </div>
        </div>
      </ModalShell>

      <ModalShell
        show={covDialog}
        title="เพิ่มพื้นที่ครอบคลุม"
        dialogClassName="modal-lg"
        titleStyle={{ fontSize: "16px", lineHeight: 1.2 }}
        onClose={() => {
          setCovDialog(false);
          setCovEditRow(null);
          setCovPendingP([]);
          setCovPendingD([]);
          setCovProvinceSearch("");
          setCovDistrictSearch("");
          setCovProvinceDistrictSearch("");
          setCovProvinceDistrictSearchApplied("");
          setProvinceDistrictCache({});
          setLoadingProvinceDistrictCache(false);
        }}
        footer={
          <>
            <button
              type="button"
              className="btn btn-light"
              style={{ borderRadius: "10px" }}
              onClick={() => {
                setCovDialog(false);
                setCovPendingP([]);
                setCovPendingD([]);
                setCovProvinceDistrictSearch("");
                setCovProvinceDistrictSearchApplied("");
                setProvinceDistrictCache({});
                setLoadingProvinceDistrictCache(false);
              }}
            >
              ยกเลิก
            </button>
            <button
              type="button"
              className="btn text-white"
              style={primaryBtnStyle}
              onClick={saveCoverageBatch}
            >
                  {covEditRow ? "บันทึกแก้ไข" : "บันทึก"}
            </button>
          </>
        }
      >
        {/* <div className="alert alert-info small py-2 mb-2">
          เลือกสาขาจาก dropdown ด้านบนตารางก่อน (ถ้ายังไม่เลือก จะใช้สาขาแรกในรายการ) — ภายในบริษัทเดียวกัน
          แต่ละจังหวัด (ระดับ P) หรือแต่ละอำเภอ (ระดับ D) ผูกได้เพียงครั้งเดียว จนกว่าจะลบจากตาราง
        </div>
        {!targetCovBranchId ? (
          <div className="alert alert-warning small py-2 mb-2">
            ยังไม่มีสาขา — สร้างสาขาในแท็บแรก (สาขา) ก่อน จึงจะเพิ่มพื้นที่ครอบคลุมได้
          </div>
        ) : null} */}
        <div className="d-flex flex-column gap-3 cov-modal-shell">
          <div style={{ display: "flex", alignItems: "center", gap: 12, width: "fit-content" }}>
            <label className="form-label mb-0">จังหวัด</label>
            <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
              <SearchableSelect
                value={covForm.Province_Id}
                placeholder="— เลือกจังหวัด —"
                searchPlaceholder="ค้นหาจังหวัด..."
                hideArrow={Boolean(covForm.Province_Id)}
              triggerClassName="cov-province-trigger"
              options={provinces.map((p) => ({
                value: String(p.Id ?? p.ProvinceId),
                label: p.NameInThai,
              }))}
                onChange={(val) => {
                  setCovPendingD([]);
                  setCovDistrictSearch("");
                  setProvinceDistrictCache({});
                  setLoadingProvinceDistrictCache(false);
                  setCovForm((f) => ({ ...f, Province_Id: val, District_Id: "" }));
                }}
                triggerStyle={{
                  borderRadius: "10px",
                  fontSize: "14px",
                  border: "1px solid #dee2e6",
                  boxShadow: "none",
                  color: "#212529",
                  width: 260,
                  height: 38,
                  minHeight: 38,
                  maxHeight: 38,
                  lineHeight: "38px",
                  padding: "0 12px",
                  display: "flex",
                  alignItems: "center",
                  boxSizing: "border-box",
                }}
              />
              {covForm.Province_Id ? (
                <button
                  type="button"
                  aria-label="ล้างจังหวัด"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCovPendingD([]);
                    setCovDistrictSearch("");
                    setProvinceDistrictCache({});
                    setLoadingProvinceDistrictCache(false);
                    setCovForm((f) => ({ ...f, Province_Id: "", District_Id: "" }));
                  }}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 26,
                    height: 26,
                    borderRadius: 999,
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    color: "#9ca3af",
                    fontSize: 18,
                    lineHeight: "26px",
                  }}
                  title="ล้างจังหวัด"
                >
                  ×
                </button>
              ) : null}
            </div>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                className="form-control"
                style={{
                  width: 240,
                  borderRadius: "10px",
                  height: 38,
                  minHeight: 38,
                  maxHeight: 38,
                  lineHeight: "38px",
                  padding: "0 12px",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  boxSizing: "border-box",
                }}
                placeholder="ค้นหาอำเภอ..."
                value={covProvinceDistrictSearch}
                onChange={(e) => setCovProvinceDistrictSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  e.stopPropagation();
                  setCovProvinceDistrictSearchApplied(String(e.currentTarget.value || "").trim());
                }}
              />
              {covProvinceDistrictSearch ? (
                <button
                  type="button"
                  aria-label="ล้างค้นหาอำเภอ"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCovProvinceDistrictSearch("");
                    setCovProvinceDistrictSearchApplied("");
                  }}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 26,
                    height: 26,
                    borderRadius: 999,
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    color: "#9ca3af",
                    fontSize: 18,
                    lineHeight: "26px",
                    padding: 0,
                  }}
                  title="ล้างค้นหา"
                >
                  ×
                </button>
              ) : null}
            </div>
          </div>

          {false && (
            <div className="w-100">
              <div className="mb-2 position-relative">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ borderRadius: "8px", paddingRight: "2rem" }}
                  placeholder="ค้นหาจังหวัด..."
                  value={covProvinceSearch}
                  onChange={(e) => setCovProvinceSearch(e.target.value)}
                />
                {covProvinceSearch && (
                  <button
                    type="button"
                    onClick={() => setCovProvinceSearch("")}
                    style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#9ca3af", cursor: "pointer", padding: 0, lineHeight: 1 }}
                  >✕</button>
                )}
              </div>
              <div className="table-responsive" style={{ maxHeight: 380, overflowY: "auto" }}>
                <table className="table table-hover mb-0" style={{ fontSize: "14px" }}>
                  <thead className="branch-red-head" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <tr>
                      <th className="text-center" style={{ width: 48 }}>
                        {(() => {
                          const bid = targetCovBranchId;
                          const filteredP = provinces.filter((p) => !covProvinceSearch || (p.NameInThai || "").includes(covProvinceSearch));
                          const selectableP = filteredP.map((p) => Number(p.Id ?? p.ProvinceId)).filter((pid) => {
                            const rowP = findProvincePRow(coverageAll, pid);
                            const onOur = rowP && bid != null && Number(rowP.Branch_Id) === bid;
                            const takenOther = rowP && bid != null && Number(rowP.Branch_Id) !== bid;
                            const anyD = hasAnyDistrictInProvince(coverageAll, pid);
                            return !Boolean(!bid || onOur || takenOther || (!rowP && anyD));
                          });
                          const allChecked = selectableP.length > 0 && selectableP.every((pid) => covPendingP.includes(pid));
                          const someChecked = !allChecked && selectableP.some((pid) => covPendingP.includes(pid));
                          return (
                            <input
                              type="checkbox"
                              className="form-check-input"
                              style={{ width: "1.1rem", height: "1.1rem", cursor: selectableP.length ? "pointer" : "not-allowed" }}
                              checked={allChecked}
                              disabled={!selectableP.length}
                              ref={(el) => { if (el) el.indeterminate = someChecked; }}
                              onChange={() => {
                                if (allChecked) {
                                  setCovPendingP((prev) => prev.filter((pid) => !selectableP.includes(pid)));
                                } else {
                                  setCovPendingP((prev) => [...new Set([...prev, ...selectableP])]);
                                }
                              }}
                            />
                          );
                        })()}
                      </th>
                      <th className="text-center" style={{ width: 48 }}>#</th>
                      <th>จังหวัด</th>
                      <th>หมายเหตุ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {provinces.length === 0 ? (
                      <tr><td colSpan={4} className="text-center text-muted py-3">กำลังโหลดจังหวัด…</td></tr>
                    ) : (
                      provinces
                        .filter((p) => !covProvinceSearch || (p.NameInThai || "").includes(covProvinceSearch))
                        .map((p, i) => {
                        const pid = Number(p.Id ?? p.ProvinceId);
                        const rowP = findProvincePRow(coverageAll, pid);
                        const bid = targetCovBranchId;
                        const onOur = rowP && bid != null && Number(rowP.Branch_Id) === bid;
                        const takenOther = rowP && bid != null && Number(rowP.Branch_Id) !== bid;
                        const anyD = hasAnyDistrictInProvince(coverageAll, pid);
                        const blocked = Boolean(takenOther || (!rowP && anyD));
                        const checked = Boolean(onOur || covPendingP.includes(pid));
                        const disabled = Boolean(!targetCovBranchId || onOur || blocked);
                        let hint = "";
                        if (takenOther) hint = `ผูกกับสาขา ${branchName(rowP.Branch_Id)} แล้ว`;
                        else if (anyD && !rowP) hint = "มีอำเภอในจังหวัดนี้แล้ว";
                        else if (onOur) hint = "มีแล้ว — ลบได้จากตารางด้านนอก";
                        return (
                          <tr
                            key={pid}
                            className={disabled ? "" : ""}
                            style={{ cursor: disabled ? "default" : "pointer" }}
                            onClick={() => !disabled && toggleCovPendingP(pid)}
                          >
                            <td className="text-center align-middle">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                style={{ width: "1.1rem", height: "1.1rem", cursor: disabled ? "not-allowed" : "pointer" }}
                                checked={checked}
                                disabled={disabled}
                                onChange={() => toggleCovPendingP(pid)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td className="text-center align-middle text-muted">{i + 1}</td>
                            <td className={`align-middle ${disabled ? "text-muted" : ""}`}>{p.NameInThai}</td>
                            <td className="align-middle text-muted small">{hint}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {covPendingP.length > 0 && <div className="cov-counter mt-2">รอบันทึก: {covPendingP.length} จังหวัด</div>}
            </div>
          )}

          {covForm.Coverage_Level === "D" && (
            <>
              <div className="w-100">
                  {/*
                    แสดงพื้นที่เผื่อได้ ~10 แถว
                    ถ้าเกินกว่านั้นจะมี scrollbar อัตโนมัติ
                  */}
                  <div
                    className="table-responsive"
                    style={{
                      // Fix ความสูงอย่างน้อยให้เท่ากับ 10 แถว (ไม่หดเมื่อมีอำเภอน้อย)
                      height: 10 * COV_TABLE_ROW_PX + COV_TABLE_HEADER_PX,
                      minHeight: 10 * COV_TABLE_ROW_PX + COV_TABLE_HEADER_PX,
                      maxHeight: 10 * COV_TABLE_ROW_PX + COV_TABLE_HEADER_PX,
                      overflowY: "auto",
                    }}
                  >
                    <table className="table table-hover mb-0" style={{ fontSize: "14px" }}>
                      <thead className="branch-red-head" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                        <tr>
                          <th className="text-center" style={{ width: 40 }}>
                            {(() => {
                              const bid = targetCovBranchId;
                              const filteredD = districtsForTable;
                              const selectableKeys = filteredD
                                .map((d) => {
                                  const pid = Number(d.__pid ?? covForm.Province_Id);
                                  const did = Number(d.Id ?? d.DistrictId);
                                  if (!pid || !did) return null;
                                  const rowD = findDistrictDRow(coverageAll, pid, did);
                                  const onOur = rowD && bid != null && Number(rowD.Branch_Id) === bid;
                                  const takenOther = rowD && bid != null && Number(rowD.Branch_Id) !== bid;
                                  const provP = findProvincePRow(coverageAll, pid);
                                  const blocked = Boolean(takenOther || provP);
                                  const disabled = Boolean(!bid || onOur || blocked);
                                  if (disabled) return null;
                                  return `${pid}:${did}`;
                                })
                                .filter(Boolean);

                              const allChecked =
                                selectableKeys.length > 0 && selectableKeys.every((k) => covPendingD.includes(k));
                              const someChecked = !allChecked && selectableKeys.some((k) => covPendingD.includes(k));
                              return (
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  style={{
                                    width: "1.1rem",
                                    height: "1.1rem",
                                    cursor: selectableKeys.length ? "pointer" : "not-allowed",
                                  }}
                                  checked={allChecked}
                                  disabled={!selectableKeys.length}
                                  ref={(el) => {
                                    if (el) el.indeterminate = someChecked;
                                  }}
                                  onChange={() => {
                                    if (allChecked) {
                                      setCovPendingD((prev) =>
                                        prev.filter((k) => !selectableKeys.includes(k))
                                      );
                                    } else {
                                      setCovPendingD((prev) => [...new Set([...prev, ...selectableKeys])]);
                                    }
                                  }}
                                />
                              );
                            })()}
                          </th>
                          <th>อำเภอ</th>
                          <th>จังหวัด</th>
                          <th>หมายเหตุ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {districtsForTable.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="text-center text-muted py-3">
                              {!covForm.Province_Id
                                ? covDistrictSearchTerm
                                  ? loadingAllDistricts
                                    ? "กำลังโหลดอำเภอ…"
                                    : "ไม่พบอำเภอ…"
                                  : covProvinceDistrictSearch
                                    ? "กด Enter เพื่อค้นหา"
                                    : "กรุณากรอกค้นหาอำเภอ"
                                : covDistrictSearchTerm
                                  ? "ไม่พบอำเภอ…"
                                  : "กำลังโหลดอำเภอ…"}
                            </td>
                          </tr>
                        ) : (
                          districtsForTable.map((d) => {
                            const pid = Number(d.__pid ?? covForm.Province_Id);
                            const did = Number(d.Id ?? d.DistrictId);
                            const rowD = findDistrictDRow(coverageAll, pid, did);
                            const bid = targetCovBranchId;
                            const onOur = rowD && bid != null && Number(rowD.Branch_Id) === bid;
                            const takenOther = rowD && bid != null && Number(rowD.Branch_Id) !== bid;
                            const provP = findProvincePRow(coverageAll, pid);
                            const blocked = Boolean(takenOther || provP);
                            const key = `${pid}:${did}`;
                            const checked = Boolean(onOur || takenOther || covPendingD.includes(key));
                            const disabled = Boolean(!targetCovBranchId || onOur || blocked);
                            const selectedProvinceName =
                              provinces.find((p) => String(p.Id ?? p.ProvinceId) === String(pid))?.NameInThai || "-";
                            return (
                              <tr
                                key={key}
                                style={{ cursor: disabled ? "default" : "pointer" }}
                                onClick={() => !disabled && toggleCovPendingD(pid, did)}
                              >
                                <td className="text-center align-middle">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    style={{ width: "1.1rem", height: "1.1rem", cursor: disabled ? "not-allowed" : "pointer" }}
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={() => toggleCovPendingD(pid, did)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </td>
                                <td
                                  className={`align-middle ${disabled ? "text-muted" : ""}`}
                                  style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                >
                                  {d.NameInThai}
                                </td>
                                <td
                                  className="align-middle text-muted"
                                  style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                >
                                  {selectedProvinceName}
                                </td>
                                <td className="align-middle" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                                  {takenOther ? (
                                    <span className="text-warning">
                                      <i className="fas fa-exclamation-triangle me-1" />
                                      อำเภอนี้อยู่ในสาขา {branchName(rowD.Branch_Id)}
                                    </span>
                                  ) : onOur ? (
                                    <span className="text-muted">
                                      มีในสาขานี้แล้ว
                                    </span>
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  {covPendingD.length > 0 && <div className="cov-counter mt-2">รอบันทึก: {covPendingD.length} อำเภอ</div>}
              </div>
            </>
          )}
        </div>
      </ModalShell>

      <ModalShell
        show={roleDialog.open}
        title={roleDialog.row ? "แก้ไขบทบาท" : "เพิ่มบทบาท"}
        onClose={() => setRoleDialog({ open: false, row: null })}
        footer={
          <>
            <button
              type="button"
              className="btn btn-light"
              style={{ borderRadius: "10px" }}
              onClick={() => setRoleDialog({ open: false, row: null })}
            >
              ยกเลิก
            </button>
            <button type="button" className="btn text-white" style={primaryBtnStyle} onClick={saveRole}>
              บันทึก
            </button>
          </>
        }
      >
        <div className="d-flex flex-column gap-3">
          <div>
            <label className="form-label">รหัสบทบาท</label>
            <input
              className="form-control"
              style={{ borderRadius: "10px" }}
              value={roleForm.Role_Code}
              onChange={(e) => setRoleForm((f) => ({ ...f, Role_Code: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">ชื่อบทบาท <span className="text-danger">*</span></label>
            <input
              className="form-control"
              style={{ borderRadius: "10px" }}
              value={roleForm.Role_Name}
              onChange={(e) => setRoleForm((f) => ({ ...f, Role_Name: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">สถานะ</label>
            <select
              className="form-select"
              style={{ borderRadius: "10px" }}
              value={roleForm.Is_Active}
              onChange={(e) => setRoleForm((f) => ({ ...f, Is_Active: e.target.value }))}
            >
              <option value="Y">ใช้งาน</option>
              <option value="N">ปิด</option>
            </select>
          </div>
        </div>
      </ModalShell>

      <ModalShell
        show={permDialog.open}
        title={`สิทธิ์บทบาท: ${permDialog.role?.Role_Name || ""}`}
        dialogClassName="modal-xl"
        onClose={() => setPermDialog({ open: false, role: null })}
        footer={
          <>
            <button
              type="button"
              className="btn btn-light"
              style={{ borderRadius: "10px" }}
              onClick={() => setPermDialog({ open: false, role: null })}
            >
              ปิด
            </button>
            <button type="button" className="btn text-white" style={primaryBtnStyle} onClick={savePerm}>
              บันทึก
            </button>
          </>
        }
      >
        <p className="small text-muted mb-3">
          เลือกสิทธิ์ CRUD ต่อหน้าเมนู — คีย์ใน DB เป็นรูปแบบ{" "}
          <code className="small">{"page.{slug}.create|read|update|delete"}</code>{" "}
          (เมนูหลักยังไม่บังคับเช็คคีย์จนกว่าจะผูกกับ Sidebar/Route)
        </p>

        {PERMISSION_MATRIX.map((section) => (
          <div key={section.title} className="mb-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
              <h6 className="fw-bold mb-0" style={{ color: "#374151" }}>
                {section.title}
              </h6>
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  style={{ borderRadius: "8px 0 0 8px" }}
                  onClick={() => toggleSectionAll(section, true)}
                >
                  เลือกครบหมวด
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  style={{ borderRadius: "0 8px 8px 0" }}
                  onClick={() => toggleSectionAll(section, false)}
                >
                  ล้างหมวด
                </button>
              </div>
            </div>
            <div className="table-responsive border rounded-3 shadow-sm">
              <table className="table table-sm table-bordered mb-0 align-middle" style={{ fontSize: "13px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#fef2f2", color: "#9f1239" }}>
                    <th className="py-2 ps-3">หน้า / เมนู</th>
                    {PERMISSION_CRUD.map((c) => (
                      <th key={c.field} className="text-center py-2" style={{ width: "76px" }}>
                        {c.label}
                      </th>
                    ))}
                    <th className="text-center py-2" style={{ width: "88px" }}>
                      ทั้งแถว
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {section.pages.map((p) => (
                    <tr key={p.id}>
                      <td className="ps-3">
                        <span className="fw-medium">{p.label}</span>
                        {p.path ? (
                          <span className="text-muted small d-block font-monospace">{p.path}</span>
                        ) : null}
                      </td>
                      {PERMISSION_CRUD.map(({ field }) => (
                        <td key={field} className="text-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={permKeysSet.has(p[field])}
                            onChange={(e) => togglePermKey(p[field], e.target.checked)}
                            title={p[field]}
                          />
                        </td>
                      ))}
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={isPageFullyChecked(p)}
                          onChange={(e) => togglePageAll(p, e.target.checked)}
                          title="เลือก/ล้าง CRUD ทั้งหมดของหน้านี้"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {extraPermissionKeys.length > 0 && (
          <div className="mb-4">
            <h6 className="fw-bold mb-2" style={{ color: "#374151" }}>
              สิทธิ์เพิ่มเติม (ไม่อยู่ในรายการมาตรฐาน)
            </h6>
            <div className="table-responsive border rounded-3">
              <table className="table table-sm table-bordered mb-0" style={{ fontSize: "13px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f3f4f6" }}>
                    <th className="py-2 ps-3">Permission_Key</th>
                    <th className="text-center py-2" style={{ width: "80px" }}>
                      ใช้งาน
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {extraPermissionKeys.map((k) => (
                    <tr key={k}>
                      <td className="ps-3 font-monospace small">{k}</td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={permKeysSet.has(k)}
                          onChange={(e) => togglePermKey(k, e.target.checked)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </ModalShell>

      <ModalShell
        show={assignEmpDialog}
        title="เพิ่มพนักงานเข้าบทบาท"
        onClose={() => { setAssignEmpDialog(false); setAssignEmpPick(""); setAssignEmpInput(""); }}
        footer={
          <>
            <button
              type="button"
              className="btn btn-light"
              style={{ borderRadius: "10px" }}
              onClick={() => { setAssignEmpDialog(false); setAssignEmpPick(""); setAssignEmpInput(""); }}
            >
              ยกเลิก
            </button>
            <button
              type="button"
              className="btn text-white"
              style={primaryBtnStyle}
              disabled={!assignEmpPick}
              onClick={assignEmpToRole}
            >
              บันทึก
            </button>
          </>
        }
      >
        <div className="d-flex flex-column gap-3">
          <div>
            <label className="form-label">ค้นหาพนักงาน</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                style={{ borderRadius: "10px 0 0 10px" }}
                placeholder="ชื่อ / รหัส / ยูสเซอร์"
                value={assignEmpInput}
                onChange={(e) => setAssignEmpInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter")
                    fetchEmployees(assignEmpInput).then(setAssignEmpOptions).catch(() => setAssignEmpOptions([]));
                }}
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                style={{ borderRadius: "0 10px 10px 0" }}
                onClick={() => fetchEmployees(assignEmpInput).then(setAssignEmpOptions).catch(() => setAssignEmpOptions([]))}
              >
                ค้นหา
              </button>
            </div>
          </div>
          <div>
            <label className="form-label">เลือกพนักงาน</label>
            <select
              className="form-select"
              style={{ borderRadius: "10px" }}
              value={assignEmpPick}
              onChange={(e) => setAssignEmpPick(e.target.value)}
            >
              <option value="">— เลือกพนักงาน —</option>
              {assignEmpOptions.map((emp) => (
                <option key={emp.Emp_Id} value={String(emp.Emp_Id)}>
                  {emp.Emp_Name}{emp.Emp_Code ? ` (${emp.Emp_Code})` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      </ModalShell>
    </div>
  );
}
